/* ============================================================
   Dathent — Post Preview screen
   Deep view from Library / Calendar. Phone-frame mock, swipeable
   carousel, fullscreen lightbox, right-side details panel with
   status-conditional actions. Source: design pack pages/post-preview.jsx.
   ============================================================ */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { isVideoAsset, resolvePosterUrl, formatDuration } from '../components/preview/normalizePost.js';
import { derivePostStatus, isPostArchived } from '../lib/postStatus.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons, BrandTints } from '../components/pack-icons/brands';
import { useActivity } from '../components/AppShell';
import { useGenerationStatus } from '../state/GenerationStatusContext.jsx';
import { platformList } from '../lib/operations.js';
import { platformDisplayName, platformFromVersions } from '../lib/platforms.js';
import { notify, friendlyErrorMessage } from '../lib/notifications.js';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { DeviceTimezoneLabel } from '../components/DeviceTimezoneLabel.jsx';
import { isRawContentHeldAwaitingImage } from '../utils/heldPost.js';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { useUpgrade } from '../contexts/UpgradeContext.jsx';
import { deriveImageAllowance } from '../lib/imageAllowance.js';
import CreationAccessNotice, { creationAccessState } from '../components/billing/CreationAccessNotice.jsx';
import { formatMetric, normalizeMetrics, reactionField } from '../lib/socialMetrics.js';
import PersistedMetrics from '../components/metrics/PersistedMetrics.jsx';
import TikTokStatusCheck from '../components/metrics/TikTokStatusCheck.jsx';
import { useFreePreview, usePreviewGuard } from '../contexts/FreePreviewContext.jsx';
import CopyTextButton from '../components/freePreview/CopyTextButton.jsx';
import PreviewCaptionEditor from '../components/freePreview/PreviewCaptionEditor.jsx';
import { canEditPreviewCaption, previewCaptionText } from '../lib/freePreview.js';
import { freePublishingState } from '../lib/freePublishing.js';
import { canEditManualDraft } from '../lib/manualDraft.js';
import { postPreviewAuthor } from '../lib/postPreviewAuthor.js';
import { Icon } from '../components/Icons.jsx';

const subscribeReadScope = listener => api.subscribeReadScope(listener);
const getReadScope = () => api.getReadScope();

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  if (v.includes('you')) return 'youtube';
  return v;
};

function fmt(n) {
  return formatMetric(n);
}
function fmtDate(d) {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function cleanPromptPrefix(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/^Generating(?:\s+\d+\s*\/\s*\d+)?\s*:\s*/i, '').trim();
}

/* Pick the most representative target for display: prefer a successfully published
   one, then any scheduled one, then whatever is first. */
function pickRepresentativeTarget(targets, opts = {}) {
  if (!targets?.length) return null;
  // Caller can specify which target/platform they navigated FROM (e.g. user
  // clicked the Telegram card in Library — they want the Telegram chrome).
  if (opts.targetId) {
    const exact = targets.find((t) => t?.id === opts.targetId);
    return exact || null;
  }
  if (opts.platform) {
    const wantPlatform = String(opts.platform).toLowerCase();
    const byPlatform = targets.find((t) => String(t?.platform || '').toLowerCase() === wantPlatform);
    if (byPlatform) return byPlatform;
  }
  return (
    targets.find((t) => t?.status === 'published' && t?.publishedAt)
    || targets.find((t) => t?.status === 'scheduled' || t?.status === 'queued')
    || targets[0]
  );
}

/* Old Threads posts were stored with two kinds of broken permalinks:
     a) placeholder "@-" handle: https://www.threads.net/@-/post/<id>
     b) numeric Meta ID instead of the public shortcode:
        https://www.threads.com/@<handle>/post/17930976774268969
        — Threads' public router only resolves SHORTCODES (DYlj8XgFECZ-style),
          not the API's internal numeric ID, so these URLs hit the "Not all
          who wander are lost…" 404.
   New posts get the proper permalink from the worker (post-publish
   permalink fetch). For broken legacy rows the frontend can't synthesize
   a shortcode out of thin air, so we degrade gracefully:
     - Good shortcode URL → return as-is.
     - Numeric-ID or @-/-placeholder URL → fall back to the author's
       profile (which always works) — better than a broken /post link.
     - No URL and no handle → return null so the UI hides the "View" CTA.
   The backend backfill task is the canonical fix for legacy rows once
   Meta returns a permalink for the post. */
function rewriteThreadsPermalink(url, platform, externalPostId, handle) {
  if (platform === 'threads') {
    return rewriteMetaPermalink(url, handle, {
      slugSegment: '/post/',
      profileBase: 'https://www.threads.com/@',
      placeholderHandleRegex: /\/@-\/post\//,
    });
  }
  if (platform === 'instagram') {
    return rewriteMetaPermalink(url, handle, {
      slugSegment: '/p/',
      profileBase: 'https://www.instagram.com/',
      placeholderHandleRegex: null,
    });
  }
  return url;
}

function safePublicationLink(value) {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.href : null;
  } catch (_) {
    return null;
  }
}

/* Shared logic for Threads + Instagram permalink degradation. Meta's public
   routers (threads.com and instagram.com) only resolve SHORTCODE URLs, not the
   numeric internal IDs returned by the Graph API at publish time. Until the
   backend backfill restores the proper shortcode, fall back to the author's
   profile URL — always works, never 404s. */
function rewriteMetaPermalink(url, handle, opts) {
  const { slugSegment, profileBase, placeholderHandleRegex } = opts;
  const cleanHandle = handle ? String(handle).replace(/^@/, '').trim() : '';
  if (typeof url !== 'string' || !url) {
    return cleanHandle ? `${profileBase}${encodeURIComponent(cleanHandle)}` : null;
  }
  // URL exists. Inspect the post slug: real Meta shortcodes have at least one
  // letter (e.g. DYlj8XgFECZ); the broken-numeric form is digits-only.
  const slugMatch = url.match(new RegExp(`${slugSegment.replace(/\//g, '\\/')}([^/?#]+)`));
  const slug = slugMatch?.[1] || '';
  const slugIsShortcode = slug && /[A-Za-z]/.test(slug);
  const hasPlaceholderHandle = placeholderHandleRegex ? placeholderHandleRegex.test(url) : false;
  if (slugIsShortcode && !hasPlaceholderHandle) {
    return url; // Good URL from Meta — leave as-is.
  }
  // Numeric/placeholder URL — degrade to profile.
  if (cleanHandle) {
    return `${profileBase}${encodeURIComponent(cleanHandle)}`;
  }
  return null;
}

/* Map a server content row into the shape our chromes expect. */
function mapServerPost(raw, targetHint = {}) {
  if (!raw) return null;
  const item = raw.contentItem || raw;
  const hasPublicationSnapshot = Boolean(raw.publicationVersion && raw.targetResolution);
  const v = hasPublicationSnapshot ? raw.publicationVersion : raw.activeVersion;
  const targets = raw.publishSummary?.targets || [];
  const resolution = raw.targetResolution;
  const resolvedTargetId = resolution?.requestedTargetId === targetHint.targetId
    ? resolution.resolvedTargetId : targetHint.targetId;
  const repTarget = pickRepresentativeTarget(targets, { ...targetHint, targetId: resolvedTargetId });
  // An explicit deep link must resolve to that target (or its linked recovery
  // successor). Falling back to the content-wide status would show a sibling's
  // success and expose actions against the wrong publication.
  if (targetHint.targetId && (!repTarget || (resolution && resolution.requestedTargetId !== targetHint.targetId))) return null;
  // Chrome priority: matching publish_target → URL hint → published →
  // scheduled → batch.platforms[0] (only present for in-flight or drafts) →
  // the item's own platform → saved version platform → legacy default.
  const batchPlatform = Array.isArray(raw?.batch?.platforms) ? raw.batch.platforms[0] : null;
  const platform = PLATFORM_FROM_NAME(
    repTarget?.platform
    || targetHint.platform
    || raw.publishSummary?.publishedPlatforms?.[0]
    || raw.publishSummary?.platforms?.[0]
    || batchPlatform
    || item.platform
    || platformFromVersions(v?.platformVersions)
  ) || targetHint.platform || batchPlatform || 'instagram';
  const tint = BrandTints[platform] || BrandTints.x;
  const fallbackGrad = [tint.from, tint.to];

  const slidesRaw = hasPublicationSnapshot ? raw.publicationCarouselSlides || [] : raw.carouselSlides || [];
  const mediaUrls = hasPublicationSnapshot ? raw.publicationMediaUrls || [] : raw.mediaUrls || [];
  // Real content format (text_post | image_post | carousel | …). `item` is
  // raw.contentItem (falls back to raw). Drives the "Media" detail label and
  // whether we synthesize a hero slide below — without it a text post fell
  // through to a phantom single slide and the details panel mislabeled it
  // "Single image".
  const format = item?.format || raw?.job?.format || '';

  let slides;
  if (slidesRaw.length > 0) {
    slides = slidesRaw.map((s, i) => {
      // The slide's attached mediaAsset is authoritative for native uploads.
      // Native video handoff currently keeps the historical image_post format,
      // so media type must come from this attachment rather than item.format.
      const mediaAsset = s.mediaAsset || null;
      const videoAsset = isVideoAsset(mediaAsset);
      const assetPublicUrl = mediaAsset?.publicUrl || mediaAsset?.public_url || null;
      const legacyImageUrl = s.imageUrl || s.publicUrl || s.public_url || mediaUrls[i] || null;
      return ({
      kind: 'image',
      cap: s.caption || '',
      grad: fallbackGrad,
      imageUrl: videoAsset ? assetPublicUrl : assetPublicUrl || legacyImageUrl,
      // Owned media-asset id (when present) → enables "save to Media library".
      mediaAssetId: mediaAsset?.id || s.mediaAssetId || s.media_asset_id || null,
      // Video passthrough — null/false for every image slide, so the
      // carousel/lightbox <video> branch stays inert for image posts.
      asset: mediaAsset,
      isVideo: videoAsset,
      publicUrl: videoAsset ? assetPublicUrl : null,
      posterUrl: videoAsset ? resolvePosterUrl(mediaAsset) : null,
      durationSeconds: videoAsset ? mediaAsset?.durationSeconds ?? mediaAsset?.duration_seconds ?? null : null,
      width: mediaAsset?.width ?? null,
      height: mediaAsset?.height ?? null,
      serif: false,
      // Show "NN / NN" counter only for multi-slide carousels.
      // Previously this was emitted unconditionally, so a single-image
      // post showed "01 / 01" — visually noisy and confusing.
      tag: slidesRaw.length > 1
        ? `${String(i + 1).padStart(2, '0')} / ${String(slidesRaw.length).padStart(2, '0')}`
        : '',
      });
    });
  } else if (mediaUrls.length > 0) {
    slides = mediaUrls.map((url, i) => ({
      kind: 'image',
      cap: '',
      grad: fallbackGrad,
      imageUrl: url,
      serif: false,
      tag: mediaUrls.length > 1
        ? `${String(i + 1).padStart(2, '0')} / ${String(mediaUrls.length).padStart(2, '0')}`
        : '',
    }));
  } else if (format !== 'text_post') {
    // image_post / carousel / unknown with no asset attached yet — keep a
    // single hero placeholder (the image may still be generating).
    const heroImage = v?.imageUrl || (hasPublicationSnapshot ? raw.publicationMediaPreview?.url : raw.mediaPreview?.url) || null;
    slides = [{
      kind: 'image',
      cap: '',
      grad: fallbackGrad,
      imageUrl: heroImage,
      serif: false,
      tag: '',
    }];
  } else {
    // Text-only post — no media frame, and no phantom "single image" in the
    // details panel (slides.length stays 0 → "Text only").
    slides = [];
  }

  // Per-platform copy when activeVersion.platformVersions has it. Backend
  // ships per-platform variants in content_versions.platform_versions_json
  // (e.g. Threads has its own 500-char tighter version). Without this every
  // platform chrome shows the same generic body and the user thinks the
  // adaptation didn't happen.
  const pv = v?.platformVersions || {};
  const pvAliasKey = platform === 'x' ? 'twitter' : platform === 'twitter' ? 'x' : null;
  const pvDirect = pv[platform];
  const pvAliased = pvAliasKey ? pv[pvAliasKey] : null;
  const platformBodyStr = (typeof pvDirect === 'string' && pvDirect.trim())
    ? pvDirect
    : (pvDirect && typeof pvDirect === 'object' && typeof pvDirect.body === 'string' && pvDirect.body.trim())
      ? pvDirect.body
      : (typeof pvAliased === 'string' && pvAliased.trim())
        ? pvAliased
        : (pvAliased && typeof pvAliased === 'object' && typeof pvAliased.body === 'string' && pvAliased.body.trim())
          ? pvAliased.body
          : null;
  const captionRaw = platformBodyStr || v?.body || v?.caption || (hasPublicationSnapshot ? '' : item.body) || '';
  const hookRaw = (platformBodyStr ? platformBodyStr.split('\n')[0] : null) || v?.hook || (hasPublicationSnapshot ? '' : item.title) || '';
  const title = cleanPromptPrefix(hookRaw || captionRaw.split('\n')[0]) || 'Untitled post';
  const caption = cleanPromptPrefix(captionRaw) || (hasPublicationSnapshot ? '' : cleanPromptPrefix(item.body || ''));

  // Hashtags: try compact platform versions, fallback to extracting from body
  const hashtags = (v?.platformVersions?.[platform]?.hashtags
    || v?.hashtags
    || extractHashtags(caption));

  const metricSource = targets.length ? repTarget?.metrics : raw.metrics;
  const metrics = metricSource || repTarget?.status === 'published' || item.status === 'published'
    ? normalizeMetrics(metricSource, repTarget?.metricFetch) : null;

  // Real handle from the target's social account (if available).
  // Prefer display_name; fall back to platform-specific username in
  // capabilities (e.g. capabilities.threadsUsername for Threads); only
  // use the numeric external_account_id as a last resort so the chrome
  // shows "@yourhandle" instead of "@123456789".
  const accountCaps = repTarget?.socialAccount?.capabilities || {};
  const fallbackUsername = (
    accountCaps.threadsUsername
    || accountCaps.instagramUsername
    || accountCaps.facebookUsername
    || accountCaps.tiktokUsername
    || accountCaps.linkedinUsername
    || accountCaps.username
    || null
  );
  const accountDisplayName = repTarget?.socialAccount?.displayName
    || (fallbackUsername ? `@${String(fallbackUsername).replace(/^@/, '')}` : null)
    || repTarget?.socialAccount?.externalAccountId
    || '';
  const accountId = repTarget?.socialAccount?.externalAccountId || '';

  // Derive the post's display status from the selected target when we
  // have one — content_item.status reflects the OVERALL post (it stays
  // 'published' if ANY target ever succeeded), but the user is here
  // looking at a SPECIFIC target. When they came in via a Telegram chrome
  // whose republish failed, showing "Published" is misleading. Fall back
  // to content_item.status only when there's no repTarget context.
  //
  // archivedAt (not any target status) is the only source of truth for
  // "archived" — see src/lib/postStatus.js. Cancelling a schedule sets the
  // representative target's status to 'cancelled' and the backend re-syncs
  // content_items.status to 'ready', WITHOUT touching archivedAt — so a
  // cancelled schedule must read as 'ready', never 'archived'.
  const status = derivePostStatus({
    targetStatus: repTarget?.status ?? null,
    targetPlatform: platform,
    targetErrorCode: repTarget?.lastErrorCode || null,
    archivedAt: item.archivedAt,
    itemStatus: item.status,
  });

  return {
    id: item.id,
    platform,
    format,
    status,
    // archivedAt is the ONLY source of truth for isArchived downstream
    // (never a status string) — see src/lib/postStatus.js.
    archivedAt: item.archivedAt || null,
    targetStatus: repTarget?.status || null,
    targetError: repTarget?.lastErrorMessage || null,
    targetErrorCode: repTarget?.lastErrorCode || null,
    scheduledAt: repTarget?.scheduledAt || raw.publishSummary?.lastScheduledAt || null,
    publishedAt: repTarget?.publishedAt || raw.publishSummary?.lastPublishedAt || null,
    failedAt: repTarget?.failedAt || raw.publishSummary?.lastFailedAt || null,
    permalink: rewriteThreadsPermalink(
      repTarget?.permalink || repTarget?.externalUrl || null,
      platform,
      repTarget?.externalPostId,
      accountDisplayName || accountId
    ),
    externalPostId: repTarget?.externalPostId || null,
    scheduledPostId: repTarget?.scheduledPostId || raw.publishSummary?.scheduledPostId || null,
    targetId: repTarget?.id || null,
    recoveryRootTargetId: repTarget?.recoveryRootTargetId || resolution?.recoveryRootTargetId || repTarget?.id || null,
    targetResolution: resolution || null,
    recoveryCandidates: Array.isArray(raw.recoveryCandidates) ? raw.recoveryCandidates : [],
    handle: accountDisplayName || (accountId ? `@${accountId.replace(/^@/, '')}` : ''),
    handleSub: '',
    title,
    caption,
    hashtags,
    altText: v?.altText || '',
    metrics,
    metricFetch: repTarget?.metricFetch || null,
    accountId: repTarget?.socialAccountId || repTarget?.socialAccount?.id || null,
    slides,
    pillar: null,
    pillarColor: null,
    createdAt: item.createdAt,
    cost: null,
  };
}

function extractHashtags(body = '') {
  const m = String(body).match(/#[\p{L}\p{N}_]+/gu);
  return m ? Array.from(new Set(m)) : [];
}

/* ============================================================
   Shared sub-components
   ============================================================ */

// Save-to-Media state shared (without prop-drilling through 7 platform chromes)
// between every MediaCarousel slide and the Lightbox.
const MediaSaveContext = createContext(null);

// Small "save this image to your Media library" button. Renders only when the
// slide is backed by an owned media asset (mediaAssetId). idle = library glyph,
// saving = dimmed/disabled, saved = check. stopPropagation so it never opens the
// lightbox / changes the slide.
function SaveToMediaButton({ slide, variant = 'slide' }) {
  const ctx = useContext(MediaSaveContext);
  const { t } = useTranslation();
  const mediaAssetId = slide?.mediaAssetId || null;
  if (!ctx || !mediaAssetId) return null;
  const { savedAssetIds, savingAssetId, saveSlideToMedia } = ctx;
  const saved = savedAssetIds.has(mediaAssetId);
  const saving = savingAssetId === mediaAssetId;
  const label = saved ? t('postPreview.savedToMedia') : t('postPreview.saveToMedia');
  const sz = variant === 'lb' ? 16 : 13;
  return (
    <button
      type="button"
      className={`pp-save${variant === 'lb' ? ' pp-save-lb' : ''}${saved ? ' is-saved' : ''}${saving ? ' is-saving' : ''}`}
      title={label}
      aria-label={label}
      disabled={saving || saved}
      onClick={(e) => { e.stopPropagation(); saveSlideToMedia(slide); }}
    >
      {saved ? <I.check size={sz} /> : <I.library size={sz} />}
    </button>
  );
}

function MediaCarousel({ slides, slideIdx, setSlideIdx, openLightbox, aspect = '1 / 1', rounded = false, full = false, contentId = null }) {
  const { t } = useTranslation();
  const { operationsArr } = useGenerationStatus();
  const videoRef = useRef(null);
  const [videoErr, setVideoErr] = useState(false);
  // One video at a time: pause + reset the previous slide's <video> when the
  // slide changes.
  useEffect(() => {
    const v = videoRef.current;
    return () => { if (v) { try { v.pause(); v.currentTime = 0; } catch (_) { /* detached */ } } };
  }, [slideIdx]);
  // Greyed "Regenerating…" overlay while an image regen for this post is in
  // flight, from the server-tracked op feed — so opening the preview from
  // Library mid-regen shows the image is being worked on too.
  const isRegenerating = !!contentId && (operationsArr || []).some(
    (op) => op.kind === 'visual' && op.status === 'running'
      && (op.contentId === contentId || (op.sourceRef?.type === 'contentItem' && op.sourceRef?.id === contentId))
  );
  if (!slides?.length) return null;
  const slide = slides[slideIdx] || slides[0];
  const next = () => setSlideIdx((i) => Math.min(slides.length - 1, i + 1));
  const prev = () => setSlideIdx((i) => Math.max(0, i - 1));

  // Attached video → real <video>, letterboxed on #0f172a; every non-video
  // slide keeps the exact backgroundImage/gradient path (byte-identical).
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;
  const showVideo = isVideoAsset(slide.asset) && !!slide.publicUrl;
  const dur = showVideo ? formatDuration(slide.durationSeconds) : null;
  const slideStyle = showVideo
    ? { backgroundColor: '#0f172a' }
    : slide.imageUrl
      ? { backgroundImage: `url("${slide.imageUrl}")` }
      : { background: `linear-gradient(135deg, ${slide.grad[0]}, ${slide.grad[1]})` };

  return (
    <div
      className={`pp-media ${rounded ? 'is-rounded' : ''} ${full ? 'is-full' : ''}`}
      style={{ aspectRatio: aspect }}
      onClick={openLightbox}
    >
      <div className="pp-media-slide" style={slideStyle}>
        {showVideo && (
          <video
            ref={videoRef}
            className="dv-player"
            src={slide.publicUrl}
            {...(slide.posterUrl ? { poster: slide.posterUrl } : {})}
            muted
            loop
            playsInline
            preload="metadata"
            autoPlay={!reducedMotion && !isMobile}
            onError={() => setVideoErr(true)}
          />
        )}
        {showVideo ? <div className="dv-scrim" /> : <div className="pp-media-grad" />}
        {slide.cap && (
          <div
            className="pp-media-cap"
            style={slide.serif ? { fontFamily: 'var(--font-serif)', fontStyle: 'italic' } : undefined}
          >
            {slide.cap.split('\n').map((line, i) => (<div key={i}>{line}</div>))}
          </div>
        )}
        {slide.tag && <span className="pp-media-tag mono">{slide.tag}</span>}
        {showVideo && (
          <>
            {(reducedMotion || isMobile) && (
              <div className="dv-play" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></div>
            )}
            <span className="dv-badge dv-badge-kind">{t('media.badge.video', { defaultValue: 'VIDEO' })}</span>
            {dur && !videoErr && <span className="dv-badge dv-badge-dur" style={{ left: 8, right: 'auto' }}>{dur}</span>}
            {videoErr && <span className="dv-err">{t('media.playbackError', { defaultValue: 'Playback error' })}</span>}
          </>
        )}
        <span className="pp-media-zoom"><I.eye size={14} /></span>
        <SaveToMediaButton slide={slide} />
        {isRegenerating && (
          <div className="ep-mp-regen" aria-live="polite">
            <span className="ep-slide-uploading-spinner" aria-hidden="true" />
            <span className="ep-mp-regen-label">{t('postPreview.regenerating')}</span>
          </div>
        )}
      </div>
      {slides.length > 1 && (
        <>
          {slideIdx > 0 && (
            <button
              className="pp-media-nav pp-media-nav-l"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              type="button"
            >
              <I.chevLeft size={16} />
            </button>
          )}
          {slideIdx < slides.length - 1 && (
            <button
              className="pp-media-nav pp-media-nav-r"
              onClick={(e) => { e.stopPropagation(); next(); }}
              type="button"
            >
              <I.chevRight size={16} />
            </button>
          )}
          <div className="pp-media-dots">
            {slides.map((_, i) => (
              <span key={i} className={`pp-media-dot ${i === slideIdx ? 'is-active' : ''}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CaptionBlock({ post, plain = false }) {
  const captionLines = (post.caption || '').split('\n');
  if (plain) {
    return (
      <div className="pp-caption pp-caption-plain">
        {captionLines.map((p, i) => (
          <span key={i}>{p}{i < captionLines.length - 1 && <br />}</span>
        ))}
        {post.hashtags?.length > 0 && (
          <div className="pp-caption-tags" style={{ marginTop: 6 }}>{post.hashtags.join(' ')}</div>
        )}
      </div>
    );
  }
  return (
    <div className="pp-caption">
      {post.handle && <span className="pp-caption-handle">{post.handle.replace('@', '')}</span>}{' '}
      {captionLines.map((p, i) => (
        <span key={i}>{p}{i < captionLines.length - 1 && <br />}</span>
      ))}
      {post.hashtags?.length > 0 && (
        <span className="pp-caption-tags"> {post.hashtags.join(' ')}</span>
      )}
    </div>
  );
}

function AuthorAvatar({ char = 'A', imageUrl, className = 'pp-avatar', style }) {
  const [failedUrl, setFailedUrl] = useState(null);
  return (
    <div className={className} style={{ background: 'linear-gradient(135deg, #D9CBB3, #A89A82)', ...style }}>
      {imageUrl && failedUrl !== imageUrl
        ? <img className="pp-author-logo" src={imageUrl} alt="" onError={() => setFailedUrl(imageUrl)} />
        : String(char || 'A').slice(0, 1).toUpperCase()}
    </div>
  );
}

/* ============================================================
   Per-platform chromes (1 per platform)
   ============================================================ */

function IGChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const { t } = useTranslation();
  const handle = post.authorName;
  return (
    <>
      <div className="pp-app-h">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">{handle}</div>
        <button type="button"><I.dots size={18} /></button>
      </div>
      <div className="pp-author">
        <AuthorAvatar char={handle.replace('@', '')[0]} imageUrl={post.authorLogoUrl} />
        <div className="pp-author-text">
          <div className="pp-author-name">{post.handle ? handle.replace('@', '') : handle}</div>
          {post.handleSub && <div className="pp-author-sub">{post.handleSub}</div>}
        </div>
        <button className="pp-follow" type="button">{t('postPreview.follow')}</button>
      </div>
      <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} contentId={post.id} aspect="1 / 1" />
      <div className="pp-ig-actions">
        <span><I.heart size={22} /></span>
        <span><I.chat size={22} /></span>
        <span><I.send size={22} /></span>
        <span style={{ marginLeft: 'auto' }}><I.flag size={22} /></span>
      </div>
      <CaptionBlock post={post} />
    </>
  );
}

function FBChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const { t } = useTranslation();
  const handle = post.authorName;
  return (
    <>
      <div className="pp-app-h pp-fb">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">{handle}</div>
        <button type="button"><I.search size={18} /></button>
      </div>
      <div className="pp-author">
        <AuthorAvatar char={handle[0]} imageUrl={post.authorLogoUrl} />
        <div className="pp-author-text">
          <div className="pp-author-name">{handle}</div>
          <div className="pp-author-sub">{t('postPreview.timeAgo2h')} · <I.globe size={9} style={{ display: 'inline', verticalAlign: 'middle' }} /></div>
        </div>
        <button className="pp-iconbtn" type="button"><I.dots size={18} /></button>
      </div>
      <CaptionBlock post={post} plain />
      <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} contentId={post.id} aspect="1 / 1" />
      {post.metrics && (
        <>
          <div className="pp-fb-counts">
          <span>👍❤️ {fmt(post.metrics[reactionField(post.metrics, post.platform)])}</span>
            <span>{t('postPreview.fbCommentsShares', { comments: fmt(post.metrics.comments), shares: fmt(post.metrics.shares) })}</span>
          </div>
        </>
      )}
      <div className="pp-fb-actions">
        <button type="button">👍 {t('postPreview.like')}</button>
        <button type="button">💬 {t('postPreview.comment')}</button>
        <button type="button">↪ {t('postPreview.share')}</button>
      </div>
    </>
  );
}

function LIChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const { t } = useTranslation();
  const handle = post.authorName;
  return (
    <>
      <div className="pp-app-h">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">{t('postPreview.post')}</div>
        <button type="button"><I.share size={16} /></button>
      </div>
      <div className="pp-author">
        <AuthorAvatar char={handle[0]} imageUrl={post.authorLogoUrl} style={{ background: 'linear-gradient(135deg, #0A66C2, #0848A0)' }} />
        <div className="pp-author-text">
          <div className="pp-author-name">{handle}</div>
          {post.handleSub && <div className="pp-author-sub">{post.handleSub}</div>}
        </div>
        <button className="pp-li-follow" type="button">+ {t('postPreview.follow')}</button>
      </div>
      <CaptionBlock post={post} plain />
      <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} contentId={post.id} aspect="1 / 1" />
      {post.metrics && (
        <div className="pp-li-stats">
          <span>{t('postPreview.liReactions', { count: fmt(post.metrics.reactionsTotal) })}</span>
          <span>·</span>
          <span>{t('postPreview.liCommentsReposts', { comments: fmt(post.metrics.comments), reposts: fmt(post.metrics.shares) })}</span>
        </div>
      )}
      <div className="pp-li-actions">
        <button type="button">👍 {t('postPreview.like')}</button>
        <button type="button">💬 {t('postPreview.comment')}</button>
        <button type="button">↻ {t('postPreview.repost')}</button>
        <button type="button">↪ {t('postPreview.send')}</button>
      </div>
    </>
  );
}

function XChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const { t } = useTranslation();
  const handle = post.authorName;
  return (
    <>
      <div className="pp-app-h pp-x">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">{t('postPreview.post')}</div>
        <button type="button"><I.dots size={18} /></button>
      </div>
      <div className="pp-x-author">
        <AuthorAvatar char={handle.replace('@', '')[0]} imageUrl={post.authorLogoUrl} />
        <div className="pp-x-author-text">
          <div className="pp-author-name">{post.handle ? handle.replace('@', '') : handle}</div>
          {post.handleSub && <div className="pp-author-sub">{post.handleSub}</div>}
        </div>
        <button className="pp-x-follow" type="button">{t('postPreview.follow')}</button>
      </div>
      <div className="pp-x-body">
        <CaptionBlock post={post} plain />
        <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} contentId={post.id} aspect="1 / 1" rounded />
      </div>
      <div className="pp-x-stamp mono">
        {post.publishedAt ? fmtDate(post.publishedAt) : post.scheduledAt ? fmtDate(post.scheduledAt) : ''}
        {(post.publishedAt || post.scheduledAt) && <> · <DeviceTimezoneLabel compact /></>}
      </div>
      {post.metrics && (
        <div className="pp-x-actions">
          <span><I.chat size={16} /> {fmt(post.metrics.comments)}</span>
          <span><I.refresh size={16} /> {fmt(post.metrics.shares)}</span>
          <span><I.heart size={16} /> {fmt(post.metrics.likes)}</span>
          <span><I.eye size={16} /> {fmt(post.metrics.views)}</span>
          <span style={{ marginLeft: 'auto' }}><I.share size={16} /></span>
        </div>
      )}
    </>
  );
}

function TTChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const { t } = useTranslation();
  const handle = post.authorName;
  const captionFirst = (post.caption || '').split('\n')[0] || post.title;
  return (
    <div className="pp-tt-wrap">
      <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} contentId={post.id} aspect="9 / 16" full />
      <div className="pp-tt-overlay">
        <div className="pp-tt-top">
          <span className="pp-tt-tab">{t('postPreview.following')}</span>
          <span className="pp-tt-tab is-active">{t('postPreview.forYou')}</span>
          <span style={{ marginLeft: 'auto' }}><I.search size={20} /></span>
        </div>
        <div className="pp-tt-bottom">
          <div className="pp-tt-text">
            <div className="pp-tt-handle">{handle}</div>
            <div className="pp-tt-caption">{captionFirst}</div>
            {post.hashtags?.length > 0 && (
              <div className="pp-tt-tags">{post.hashtags.join(' ')}</div>
            )}
          </div>
          <div className="pp-tt-side">
            <AuthorAvatar className="pp-tt-avatar" char={handle.replace('@', '')[0]} imageUrl={post.authorLogoUrl} />
            {post.metrics && (
              <>
                <div className="pp-tt-act"><I.heart size={26} /><span>{fmt(post.metrics.likes)}</span></div>
                <div className="pp-tt-act"><I.chat size={26} /><span>{fmt(post.metrics.comments)}</span></div>
                <div className="pp-tt-act"><I.share size={26} /><span>{fmt(post.metrics.shares)}</span></div>
              </>
            )}
            <div className="pp-tt-act"><I.flag size={26} /><span>{t('postPreview.save')}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TGChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const handle = post.authorName;
  return (
    <>
      <div className="pp-app-h pp-tg">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-tg-h">
          <AuthorAvatar char={handle[0]} imageUrl={post.authorLogoUrl} style={{ width: 28, height: 28, fontSize: 12 }} />
          <div>
            <div className="pp-ig-title">{handle}</div>
            {post.handleSub && <div className="pp-author-sub" style={{ fontSize: 11 }}>{post.handleSub}</div>}
          </div>
        </div>
        <button type="button"><I.dots size={18} /></button>
      </div>
      <div className="pp-tg-msg">
        <div className="pp-tg-bubble">
          <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} contentId={post.id} aspect="1 / 1" rounded />
          <div style={{ padding: '10px 12px' }}>
            <CaptionBlock post={post} plain />
            {post.metrics && (
              <div className="pp-tg-meta">
                <span>👁 {fmt(post.metrics.views)}</span>
                <span className="mono">{post.publishedAt ? fmtDate(post.publishedAt) : ''}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function ThreadsChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const handle = post.authorName;
  // Only render the media tile when we actually have a real image. Threads
  // posts default to text_post in the generation pipeline, but legacy
  // batches and any backend code that seeds a placeholder slide row
  // would otherwise force MediaCarousel to render a black/empty square
  // inside the chrome.
  const { t } = useTranslation();
  const visibleSlides = (slides || []).filter((slide) => slide?.imageUrl && !slide.imageIsPlaceholder);
  return (
    <>
      <div className="pp-app-h pp-th">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">{t('postPreview.thread')}</div>
        <button type="button"><I.dots size={18} /></button>
      </div>
      <div className="pp-th-post">
        <div className="pp-th-rail">
          <AuthorAvatar char={handle.replace('@', '')[0]} imageUrl={post.authorLogoUrl} />
        </div>
        <div className="pp-th-body">
          <div className="pp-th-head">
            <span className="pp-th-handle">{post.handle ? handle.replace('@', '') : handle}</span>
            <span className="pp-author-sub" style={{ marginLeft: 6 }}>{t('postPreview.timeAgo2h')}</span>
            <span style={{ marginLeft: 'auto' }}><I.dots size={14} /></span>
          </div>
          <CaptionBlock post={post} plain />
          {visibleSlides.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <MediaCarousel slides={visibleSlides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} contentId={post.id} aspect="1 / 1" rounded />
            </div>
          )}
          <div className="pp-th-actions">
            <span><I.heart size={18} /></span>
            <span><I.chat size={18} /></span>
            <span><I.refresh size={18} /></span>
            <span><I.send size={18} /></span>
          </div>
          {post.metrics && (
            <div className="pp-th-counts">{t('postPreview.thLikesReplies', { likes: fmt(post.metrics.likes), replies: fmt(post.metrics.comments) })}</div>
          )}
        </div>
      </div>
    </>
  );
}

/* Phone frame composes the right chrome */
function PhoneFrame({ post, slideIdx, setSlideIdx, openLightbox }) {
  const chromes = {
    instagram: IGChrome,
    facebook: FBChrome,
    linkedin: LIChrome,
    x: XChrome,
    tiktok: TTChrome,
    telegram: TGChrome,
    threads: ThreadsChrome,
  };
  const Chrome = chromes[post.platform] || IGChrome;
  return (
    <div className="pp-phone">
      <div className="pp-phone-shell">
        <div className="pp-phone-notch" />
        <div className={`pp-phone-screen pp-platform-${post.platform}`}>
          <div className="pp-phone-status">
            <span className="mono">9:41</span>
            <span className="pp-phone-status-r">
              <svg width="14" height="9" viewBox="0 0 14 9" fill="currentColor"><circle cx="1.5" cy="7.5" r="1.5" /><circle cx="5.5" cy="6" r="1.5" /><circle cx="9.5" cy="3.5" r="1.5" /></svg>
              <svg width="13" height="9" viewBox="0 0 13 9" fill="currentColor"><path d="M0 4.5L6.5 0L13 4.5L11.5 6L6.5 2L1.5 6L0 4.5Z" /></svg>
              <svg width="18" height="9" viewBox="0 0 18 9" fill="none" stroke="currentColor" strokeWidth="0.8"><rect x="0.4" y="0.4" width="14" height="8.2" rx="1.5" /><rect x="2" y="2" width="11" height="5" rx="0.5" fill="currentColor" /><rect x="15.2" y="3" width="1.4" height="3" rx="0.5" fill="currentColor" /></svg>
            </span>
          </div>
          <Chrome post={post} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} slides={post.slides} />
          <div className="pp-phone-home" />
        </div>
      </div>
    </div>
  );
}

/* Lightbox with Esc + arrow nav */
function Lightbox({ post, slideIdx, setSlideIdx, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setSlideIdx((i) => Math.min(post.slides.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setSlideIdx((i) => Math.max(0, i - 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [post, onClose, setSlideIdx]);

  const slide = post.slides[slideIdx];
  // The lightbox is the attached video's dedicated player with native controls.
  // Images keep the backgroundImage path.
  const showVideo = isVideoAsset(slide.asset) && !!slide.publicUrl;
  const imgStyle = showVideo
    ? { backgroundColor: '#0f172a' }
    : slide.imageUrl
      ? { backgroundImage: `url("${slide.imageUrl}")` }
      : { background: `linear-gradient(135deg, ${slide.grad[0]}, ${slide.grad[1]})` };

  return (
    <div className="pp-lightbox" onClick={onClose}>
      <button className="pp-lb-close" onClick={onClose} type="button"><I.x size={18} /></button>
      <div className="pp-lb-img" onClick={(e) => e.stopPropagation()} style={imgStyle}>
        {showVideo && (
          <video
            className="dv-player"
            src={slide.publicUrl}
            {...(slide.posterUrl ? { poster: slide.posterUrl } : {})}
            muted
            loop
            playsInline
            preload="metadata"
            controls
          />
        )}
        {showVideo && <div className="dv-scrim" />}
        {slide.cap && (
          <div className="pp-lb-cap" style={slide.serif ? { fontFamily: 'var(--font-serif)', fontStyle: 'italic' } : undefined}>
            {slide.cap.split('\n').map((line, i) => <div key={i}>{line}</div>)}
          </div>
        )}
        {slide.tag && <span className="pp-lb-tag mono">{slide.tag}</span>}
        <SaveToMediaButton slide={slide} variant="lb" />
      </div>
      {post.slides.length > 1 && (
        <div className="pp-lb-bar" onClick={(e) => e.stopPropagation()}>
          <button
            className="pp-lb-btn" disabled={slideIdx === 0}
            onClick={() => setSlideIdx((i) => Math.max(0, i - 1))} type="button"
          >
            <I.chevLeft size={14} /> {t('postPreview.prev')}
          </button>
          <div className="pp-lb-dots">
            {post.slides.map((_, i) => (
              <button
                key={i} className={`pp-lb-dot ${i === slideIdx ? 'is-active' : ''}`}
                onClick={() => setSlideIdx(i)} type="button" aria-label={t('postPreview.slideN', { number: i + 1 })}
              />
            ))}
          </div>
          <button
            className="pp-lb-btn" disabled={slideIdx === post.slides.length - 1}
            onClick={() => setSlideIdx((i) => Math.min(post.slides.length - 1, i + 1))} type="button"
          >
            {t('postPreview.next')} <I.chevRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Shared revoked-account UX. Used by every platform insights/replies
   panel (Threads, IG) when the proxied call comes back with an error
   that means "your access token is no longer valid" (revoked, expired,
   missing permission, etc.). Renders a calm yellow-tinted notice with
   a one-click "Reconnect" link to the right Settings section instead
   of a raw "Instagram account is revoked" string.
   ============================================================ */
const REVOKED_PATTERNS = [
  /revoke/i,
  /expir/i,
  /invalid.*token/i,
  /reconnect/i,
  /reauthor/i,
  /401/,
  /access\s*denied/i,
  /not\s*authoriz/i,
];

function isAccountRevokedError(err) {
  if (!err) return false;
  const msg = typeof err === 'string' ? err : err.message || '';
  return REVOKED_PATTERNS.some((re) => re.test(msg));
}

function AccountRevokedNotice({ platform, message }) {
  const { t } = useTranslation();
  const label = platform === 'instagram'
    ? 'Instagram'
    : platform === 'threads'
    ? 'Threads'
    : platform === 'facebook'
    ? 'Facebook'
    : platform === 'tiktok'
    ? 'TikTok'
    : platform === 'x' || platform === 'twitter'
    ? 'X'
    : platform === 'telegram'
    ? 'Telegram'
    : t('postPreview.thisAccount');
  return (
    <div
      role="note"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 8,
        padding: '10px 12px',
        background: 'color-mix(in srgb, var(--warning, var(--accent)) 9%, var(--bg-elev))',
        border: '1px solid color-mix(in srgb, var(--warning, var(--accent)) 28%, var(--border-subtle))',
        borderRadius: 'var(--r-sm)',
        fontSize: 12.5,
        color: 'var(--text-muted)',
        lineHeight: 1.4,
      }}
    >
      <I.alert size={14} style={{ flex: '0 0 auto', color: 'var(--warning, var(--accent))' }} aria-hidden="true" />
      <span style={{ flex: '1 1 auto' }}>
        {t('postPreview.accountRevokedBody', { label })}
        {message && message.trim() && !isAccountRevokedError(message)
          ? <span style={{ display: 'block', opacity: 0.7, marginTop: 4, fontSize: 11.5 }}>{message}</span>
          : null}
      </span>
      <Link
        to="/app/settings?tab=social"
        style={{
          flex: '0 0 auto',
          whiteSpace: 'nowrap',
          color: 'var(--accent-text)',
          textDecoration: 'none',
          fontWeight: 500,
          fontSize: 12.5,
        }}
      >
        {t('postPreview.reconnect')} →
      </Link>
    </div>
  );
}

/* ============================================================
   Threads-specific panels: live insights + replies inbox.
   Visible only when the post lives on Threads and has an external
   post id (= it's published).
   ============================================================ */
function ThreadsRepliesSection({ externalPostId, postOwnerHandle }) {
  const { t } = useTranslation();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [hidingId, setHidingId] = useState(null);

  useEffect(() => {
    if (!externalPostId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.threads.listReplies(externalPostId)
      .then((res) => { if (!cancelled) setReplies(res?.replies || []); })
      .catch((err) => { if (!cancelled) setError(err.message || t('postPreview.couldNotLoadReplies')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [externalPostId, refreshKey]);

  if (!externalPostId) return null;

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    // Optimistic insert — show the reply right away with a "Sending…"
    // badge so the UI feels instant. The real reply replaces it once
    // the server confirms (refreshKey re-fetches the canonical list).
    const optimisticId = `__optimistic_${Date.now()}`;
    setReplies((prev) => [
      { id: optimisticId, text, username: prev[0]?.username || 'you', timestamp: new Date().toISOString(), hidden: false, permalink: null, pending: true },
      ...prev,
    ]);
    setDraft('');
    try {
      await api.threads.sendReply(externalPostId, text);
      // Refresh — server-side reply will replace the optimistic row.
      setRefreshKey((k) => k + 1);
    } catch (err) {
      // Roll back the optimistic row and surface the failure inline.
      setReplies((prev) => prev.filter((r) => r.id !== optimisticId));
      setDraft(text);
      setError(err?.message || t('postPreview.replyFailed'));
    } finally {
      setSending(false);
    }
  };

  const toggleHide = async (reply) => {
    setHidingId(reply.id);
    try {
      if (reply.hidden) {
        await api.threads.unhideReply(reply.id);
      } else {
        await api.threads.hideReply(reply.id);
      }
      setRefreshKey((k) => k + 1);
    } catch (err) {
      notify({ level: 'error', title: t('postPreview.updateFailed', { error: friendlyErrorMessage(err) }) });
    } finally {
      setHidingId(null);
    }
  };

  return (
    <div className="pp-section">
      <div className="eyebrow"><span>{t('postPreview.threadsReplies')}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 className="pp-section-h" style={{ margin: 0 }}>{t('postPreview.conversation')}</h3>
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
        >
          <I.refresh size={12} /> {t('postPreview.refresh')}
        </button>
      </div>

      {/* Reply composer — textarea above, action row below for stable
          alignment regardless of how tall the user grows the textarea. */}
      <div
        style={{
          marginBottom: 14,
          padding: 10,
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-elev)',
          transition: 'border-color var(--dur-fast), box-shadow var(--dur-fast)',
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('postPreview.replyAsAuthor')}
          rows={2}
          disabled={sending}
          style={{
            width: '100%',
            border: 0,
            outline: 0,
            background: 'transparent',
            color: 'var(--text)',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            lineHeight: 1.5,
            resize: 'vertical',
            minHeight: 44,
            padding: 0,
          }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border-subtle)',
        }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
            {/* "Posts as" must show the CONNECTED ACCOUNT (post owner), not
                the latest commenter's handle. Reading replies[0]?.username
                was a bug — the first reply could be from anyone, including
                the user who just commented from another account, which
                made the reply box claim it would post as THEM. Use the
                postOwnerHandle prop (passed from the page's post.handle). */}
            {draft.length > 0
              ? `${draft.length} / 500`
              : t('postPreview.postsAs', { handle: postOwnerHandle ? (postOwnerHandle.startsWith('@') ? postOwnerHandle : '@' + postOwnerHandle) : '@you' })}
          </span>
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            style={{ minWidth: 84, justifyContent: 'center' }}
          >
            {sending ? (
              <>
                <span className="dot pulse" style={{ background: 'currentColor', width: 6, height: 6 }} />
                {t('postPreview.sending')}
              </>
            ) : (
              <><I.send size={12} /> {t('postPreview.send')}</>
            )}
          </button>
        </div>
      </div>

      {loading && <div className="muted" style={{ fontSize: 13 }}>{t('postPreview.loadingReplies')}</div>}
      {error && isAccountRevokedError(error) ? (
        <AccountRevokedNotice platform="threads" message={error} />
      ) : error ? (
        <div className="auth-error">
          <I.alert size={14} /> <span>{error}</span>
        </div>
      ) : null}
      {!loading && !error && replies.length === 0 && (
        <div className="muted" style={{ fontSize: 13 }}>{t('postPreview.noRepliesYet')}</div>
      )}
      {!loading && replies.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {replies.map((r) => (
            <li
              key={r.id}
              style={{
                padding: 10,
                background: r.hidden ? 'var(--bg-subtle)' : 'var(--bg-elev)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--r-md)',
                opacity: r.pending ? 0.7 : r.hidden ? 0.55 : 1,
                transition: 'opacity var(--dur-fast), background var(--dur-fast)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4, gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>@{r.username || 'user'}</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {r.pending && <span className="dot pulse" style={{ background: 'var(--accent)', width: 6, height: 6 }} />}
                  {r.pending ? t('postPreview.sendingEllipsis') : (r.timestamp ? fmtDate(r.timestamp) : '')}
                </span>
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.4, color: 'var(--text)' }}>
                {r.text || <em style={{ color: 'var(--text-subtle)' }}>{t('postPreview.noText')}</em>}
              </div>
              {!r.pending && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                  {r.permalink && (
                    <a
                      className="btn btn-ghost btn-sm"
                      href={r.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <I.external size={11} /> {t('postPreview.view')}
                    </a>
                  )}
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => toggleHide(r)}
                    disabled={hidingId === r.id}
                  >
                    {r.hidden ? <><I.eye size={11} /> {t('postPreview.unhide')}</> : <><I.alert size={11} /> {t('postPreview.hide')}</>}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================
   Instagram-specific panels: insights + comments.
   Mirrors the Threads pair but talks to api.instagram.* and uses
   Meta Graph IG Business endpoint metrics (impressions, reach, …).
   Visible only when the post is on Instagram and has an external
   media ID (= it's published).
   ============================================================ */
function InstagramCommentsSection({ externalPostId, postOwnerHandle }) {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [hidingId, setHidingId] = useState(null);

  useEffect(() => {
    if (!externalPostId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.instagram.listComments(externalPostId)
      .then((res) => { if (!cancelled) setComments(res?.comments || []); })
      .catch((err) => { if (!cancelled) setError(err.message || t('postPreview.couldNotLoadComments')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [externalPostId, refreshKey]);

  if (!externalPostId) return null;

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    const optimisticId = `__optimistic_${Date.now()}`;
    setComments((prev) => [
      { id: optimisticId, text, username: postOwnerHandle ? postOwnerHandle.replace(/^@/, '') : 'you', timestamp: new Date().toISOString(), hidden: false, pending: true },
      ...prev,
    ]);
    setDraft('');
    try {
      await api.instagram.sendComment(externalPostId, text);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setComments((prev) => prev.filter((c) => c.id !== optimisticId));
      setDraft(text);
      setError(err?.message || t('postPreview.commentFailed'));
    } finally {
      setSending(false);
    }
  };

  const toggleHide = async (comment) => {
    setHidingId(comment.id);
    try {
      if (comment.hidden) {
        await api.instagram.unhideComment(comment.id);
      } else {
        await api.instagram.hideComment(comment.id);
      }
      setRefreshKey((k) => k + 1);
    } catch (err) {
      notify({ level: 'error', title: t('postPreview.updateFailed', { error: friendlyErrorMessage(err) }) });
    } finally {
      setHidingId(null);
    }
  };

  return (
    <div className="pp-section">
      <div className="eyebrow"><span>{t('postPreview.instagramComments')}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 className="pp-section-h" style={{ margin: 0 }}>{t('postPreview.conversation')}</h3>
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
        >
          <I.refresh size={12} /> {t('postPreview.refresh')}
        </button>
      </div>

      <div
        style={{
          marginBottom: 14,
          padding: 10,
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-elev)',
          transition: 'border-color var(--dur-fast), box-shadow var(--dur-fast)',
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('postPreview.commentAsAuthor')}
          rows={2}
          disabled={sending}
          style={{
            width: '100%',
            border: 0,
            outline: 0,
            background: 'transparent',
            color: 'var(--text)',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            lineHeight: 1.5,
            resize: 'vertical',
            minHeight: 44,
            padding: 0,
          }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border-subtle)',
        }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
            {draft.length > 0
              ? `${draft.length} / 2200`
              : t('postPreview.postsAs', { handle: postOwnerHandle ? (postOwnerHandle.startsWith('@') ? postOwnerHandle : '@' + postOwnerHandle) : '@you' })}
          </span>
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            style={{ minWidth: 84, justifyContent: 'center' }}
          >
            {sending ? (
              <>
                <span className="dot pulse" style={{ background: 'currentColor', width: 6, height: 6 }} />
                {t('postPreview.sending')}
              </>
            ) : (
              <><I.send size={12} /> {t('postPreview.send')}</>
            )}
          </button>
        </div>
      </div>

      {loading && <div className="muted" style={{ fontSize: 13 }}>{t('postPreview.loadingComments')}</div>}
      {error && isAccountRevokedError(error) ? (
        <AccountRevokedNotice platform="instagram" message={error} />
      ) : error ? (
        <div className="auth-error">
          <I.alert size={14} /> <span>{error}</span>
        </div>
      ) : null}
      {!loading && !error && comments.length === 0 && (
        <div className="muted" style={{ fontSize: 13 }}>{t('postPreview.noCommentsYet')}</div>
      )}
      {!loading && comments.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {comments.map((c) => (
            <li
              key={c.id}
              style={{
                padding: 10,
                background: c.hidden ? 'var(--bg-subtle)' : 'var(--bg-elev)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--r-md)',
                opacity: c.pending ? 0.7 : c.hidden ? 0.55 : 1,
                transition: 'opacity var(--dur-fast), background var(--dur-fast)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4, gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>@{c.username || 'user'}</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {c.pending && <span className="dot pulse" style={{ background: 'var(--accent)', width: 6, height: 6 }} />}
                  {c.pending ? t('postPreview.sendingEllipsis') : (c.timestamp ? fmtDate(c.timestamp) : '')}
                </span>
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.4, color: 'var(--text)' }}>
                {c.text || <em style={{ color: 'var(--text-subtle)' }}>{t('postPreview.noText')}</em>}
              </div>
              {!c.pending && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => toggleHide(c)}
                    disabled={hidingId === c.id}
                  >
                    {c.hidden ? <><I.eye size={11} /> {t('postPreview.unhide')}</> : <><I.alert size={11} /> {t('postPreview.hide')}</>}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* Details panel — status-conditional actions + metrics */
function DetailsPanel({ post, compactPreview = false, publishingOp, onPublishNow, onRepublish, onSchedule, onEdit, onDelete, onReschedule, onRetry, onReload, onLinkExisting }) {
  const { t } = useTranslation();
  const isPublished = post.status === 'published';
  const isFailed = post.status === 'failed';
  const isInbox = post.status === 'inbox';
  const needsCheck = post.status === 'needs_check';
  // Treat the post as "publishing now" when either:
  //  1. There's an active publish op tracked in GenerationStatusContext
  //     (mints the publishing-state during the awkward sliver between
  //     a manual "Publish now" click and the cron picking it up), OR
  //  2. The server reports the post in 'publishing' / 'queued' state
  //     (e.g. it just landed there from an onboarding-activation
  //     publish_now dispatch — no client op was created, but the
  //     target is already moving through the publish queue).
  const targetIsTerminal = ['published', 'failed', 'needs_reconnect', 'needs_manual_action', 'cancelled'].includes(post.targetStatus);
  const opTargetsPlatform = Boolean(publishingOp)
    && (!publishingOp.platforms?.length || publishingOp.platforms.includes(post.platform))
    && (!publishingOp.platformStatus?.[post.platform] || publishingOp.platformStatus[post.platform] === 'running');
  const isPublishingNow = (!targetIsTerminal && opTargetsPlatform)
    || post.status === 'publishing'
    || post.targetStatus === 'publishing'
    || post.targetStatus === 'queued';
  const isScheduled = !isPublishingNow && !isFailed && post.status === 'scheduled';
  const isReady = post.status === 'ready';
  // archivedAt is the only source of truth for "archived" — never a status
  // string (target or item). See src/lib/postStatus.js.
  const isArchived = isPostArchived(post.archivedAt);
  const isDraft = post.status === 'draft' || isReady;
  const linkableCandidates = post.recoveryCandidates.filter((candidate) => candidate.targetId !== post.targetId
    && candidate.platform === post.platform && candidate.socialAccountId === post.accountId
    && candidate.publishedAt);

  const statusBadge = isPublishingNow
    ? <span className="pill pill-info"><span className="dot pulse" />{t('postPreview.statusPublishing')}</span>
      : isPublished
        ? <span className="pill pill-success"><span className="dot" />{t('postPreview.statusPublished')}</span>
      : isInbox
        ? <span className="pill pill-inbox"><span className="dot" />{t('postPreview.inboxDraftTitle')}</span>
      : needsCheck
        ? <span className="pill pill-warning"><span className="dot" />{t('postPreview.statusNeedsCheck')}</span>
      : isFailed
        ? <span className="pill pill-warning"><span className="dot" />{t('postPreview.statusFailed')}</span>
        : isScheduled
        ? <span className="pill pill-info"><span className="dot" />{t('postPreview.statusScheduled')}</span>
        : isReady
          ? <span className="pill pill-info"><span className="dot" />{t('postPreview.statusReady')}</span>
          : isArchived
            ? <span className="pill"><span className="dot" />{t('postPreview.statusArchived')}</span>
            : <span className="pill"><span className="dot" />{t('postPreview.statusDraft')}</span>;

  const Metadata = compactPreview ? 'details' : 'div';
  return (
    <aside className={`pp-details${compactPreview ? ' pp-details--preview' : ''}`}>
      <div className="pp-d-h">
        <div className="pp-d-h-l">
          {compactPreview ? <span className="pill">{t('reveal.draftBadge')}</span> : statusBadge}
          {!compactPreview && <h2 className="pp-d-h-title">{post.title}</h2>}
          <div className="pp-d-h-sub">
            <span
              className={`ob-net-ic-${post.platform}`}
              style={{ width: 18, height: 18, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
            >
              {BrandIcons[post.platform]?.({ size: 11 })}
            </span>
            <span>{platformDisplayName(post.platform)}</span>
            {post.handle && (
              <>
                <span className="pp-d-sep">·</span>
                <span>{post.handle}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="pp-actions">
        {isPublishingNow && (
          <>
            <div className="pp-action-meta">
              <span className="pp-action-l">{publishingOp?.label || t('postPreview.publishingNow')}</span>
              <span className="pp-action-v">
                <span className="dot pulse" style={{ background: 'var(--accent)' }} />
                {publishingOp?.total
                  ? t('postPreview.platformsProgress', { completed: publishingOp?.completed || 0, total: publishingOp?.total })
                  : t('postPreview.sendingToPlatform')}
              </span>
            </div>
            {publishingOp?.platforms?.length > 0 && (
              <div className="pp-action-row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {publishingOp.platforms.map((p) => {
                  const s = publishingOp.platformStatus?.[p] || 'running';
                  const Icon = s === 'succeeded' ? I.check : s === 'failed' ? I.x : null;
                  return (
                    <span key={p} className={`pill pill-${s === 'succeeded' ? 'success' : s === 'failed' ? 'warning' : 'info'}`}>
                      {Icon ? <Icon size={11} /> : <span className="dot pulse" />}
                      {platformDisplayName(p)}
                    </span>
                  );
                })}
              </div>
            )}
          </>
        )}

        {isScheduled && (
          <>
            <div className="pp-action-meta">
              <span className="pp-action-l">{t('postPreview.scheduledFor')}</span>
              <span className="pp-action-v"><I.clock size={13} /> {fmtDate(post.scheduledAt) || t('postPreview.tbd')} {post.scheduledAt && <DeviceTimezoneLabel compact />}</span>
            </div>
            <button className="btn btn-primary btn-lg pp-action-cta" onClick={onPublishNow} type="button">
              <I.zap size={14} /> {t('postPreview.publishNow')}
            </button>
            <div className="pp-action-row">
              <button className="btn btn-ghost btn-sm" onClick={onEdit} type="button">
                <I.edit size={12} /> {t('postPreview.edit')}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onReschedule} type="button">
                <I.calendar size={12} /> {t('postPreview.reschedule')}
              </button>
              {!post.targetId && onDelete && <button className="btn btn-ghost btn-sm pp-danger" onClick={onDelete} type="button">
                <I.trash size={12} /> {t('postPreview.delete')}
              </button>}
            </div>
          </>
        )}

        {isPublished && (
          <>
            <div className="pp-action-meta">
              <span className="pp-action-l">{t('postPreview.publishedLabel')}</span>
              <span className="pp-action-v"><I.clock size={13} /> {fmtDate(post.publishedAt) || t('postPreview.recently')}</span>
            </div>
            {/* Was "Publish again" → cloned the scheduled_post and
                published immediately to the same platform. Users
                expected a creative restart (edit copy, pick a
                different account). Renamed + rewired to deep-link
                into EditPost's Schedule tab where every dimension
                is editable before Save. */}
            <button className="btn btn-primary btn-lg pp-action-cta" onClick={onRepublish} type="button">
              <I.edit size={14} /> {t('postPreview.editAndRepublish')}
            </button>
            <div className="pp-action-row">
              {post.permalink && (
                <a
                  className="btn btn-ghost btn-sm"
                  href={post.permalink.startsWith('http') ? post.permalink : `https://${post.permalink}`}
                  target="_blank" rel="noopener noreferrer"
                >
                  <I.external size={12} /> {t('postPreview.viewOnPlatform', { platform: platformDisplayName(post.platform) })}
                </a>
              )}
              {/* A target preview cannot delete one platform publication by
                  archiving its shared content item. Whole-content archive
                  remains available from the editor's Advanced section. */}
            </div>
          </>
        )}

        {isInbox && (
          <div className="pp-inbox-note">
            <strong>{t('postPreview.inboxDraftTitle')}</strong>
            <span>{post.targetError || t('postPreview.inboxDraftBody')}</span>
          </div>
        )}

        {needsCheck && (
          <div className="pp-inbox-note">
            <strong>{t('postPreview.statusNeedsCheck')}</strong>
            <span>{post.platform === 'tiktok' && post.targetErrorCode === 'TIKTOK_PUBLIC_ID_UNAVAILABLE'
              ? t('postPreview.tiktokPublicLinkUnconfirmed', { defaultValue: 'A public TikTok link is not confirmed yet. Check status or view the video in TikTok before posting again.' })
              : post.targetError || t('postPreview.unknownPublishOutcome', { platform: platformDisplayName(post.platform) })}</span>
            {post.platform === 'tiktok' && <TikTokStatusCheck key={`${post.id}:${post.targetId || ''}:${api.getReadOwnerScope()}`} postId={post.id} onReload={onReload} />}
            {post.platform === 'threads' && post.targetId && post.targetErrorCode === 'PUBLISH_OUTCOME_UNKNOWN' && (
              <button className="btn btn-ghost btn-sm" onClick={onRepublish} type="button">
                <I.edit size={12} /> {t('postPreview.editAndRepublish')}
              </button>
            )}
          </div>
        )}

        {isFailed && (
          <>
            <div className="pp-action-meta">
              <span className="pp-action-l">{t('postPreview.platformPublishFailed', { platform: platformDisplayName(post.platform) })}</span>
              <span className="pp-action-v"><I.alert size={13} /> {fmtDate(post.failedAt) || t('postPreview.justNow')}</span>
            </div>
            {post.targetError && (
              <div className="auth-error" style={{ marginTop: 8, marginBottom: 12, fontSize: 12, lineHeight: 1.45 }}>
                <I.alert size={13} /> <span>{post.targetError}</span>
              </div>
            )}
            <button className="btn btn-primary btn-lg pp-action-cta" onClick={onRetry || onRepublish} type="button">
              <I.refresh size={14} /> {t('postPreview.retryPublish')}
            </button>
            <div className="pp-action-row">
              <button className="btn btn-ghost btn-sm" onClick={onEdit} type="button">
                <I.edit size={12} /> {t('postPreview.edit')}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onRepublish} type="button">
                <I.send size={12} /> {t('postPreview.editAndRepublish')}
              </button>
              {!post.targetId && onDelete && <button className="btn btn-ghost btn-sm pp-danger" onClick={onDelete} type="button">
                <I.trash size={12} /> {t('postPreview.delete')}
              </button>}
            </div>
          </>
        )}

        {(needsCheck || isFailed) && post.targetId && linkableCandidates.length > 0 && (
          <div className="pp-inbox-note" role="group" aria-label={t('postPreview.existingPublicationTitle')}>
            <strong>{t('postPreview.existingPublicationTitle')}</strong>
            <span>{t('postPreview.existingPublicationHint')}</span>
            {linkableCandidates.map((candidate) => (
              <div key={candidate.targetId} className="pp-recovery-candidate">
                <span>{candidate.accountLabel || post.handle} · {fmtDate(candidate.publishedAt)}</span>
                {safePublicationLink(candidate.permalink)
                  ? <a href={safePublicationLink(candidate.permalink)} target="_blank" rel="noopener noreferrer">
                    {t('postPreview.viewOnPlatform', { platform: platformDisplayName(post.platform) })}
                  </a>
                  : <small>{t('postPreview.existingPublicationNoLink')}</small>}
                <button className="btn btn-ghost btn-sm" onClick={() => onLinkExisting(candidate)} type="button">
                  {t('postPreview.useExistingPublication')}
                </button>
              </div>
            ))}
          </div>
        )}

        {isDraft && compactPreview && (
          <>
            <div className="pp-action-row pp-preview-edit-actions">
              <button className="btn btn-primary" onClick={onEdit} type="button">
                <I.edit size={14} /> {t('freePreview.captionTitle')}
              </button>
              <CopyTextButton text={previewCaptionText(post)} />
            </div>
            <div className="pp-preview-schedule">
              <button className="btn btn-ghost btn-sm" onClick={onSchedule} type="button">
                <I.calendar size={14} /> {t('postPreview.scheduleThisPost')}
              </button>
              <p>{t('freePreview.actionHint')}</p>
            </div>
          </>
        )}
        {isDraft && !compactPreview && (
          <>
            <div className="pp-action-meta">
              <span className="pp-action-l">{isReady ? t('postPreview.statusReady') : t('postPreview.statusDraft')}</span>
              <span className="pp-action-v"><I.clock size={13} /> {t('postPreview.savedDate', { date: fmtDate(post.createdAt) || '' })}</span>
            </div>
            <button className="btn btn-primary btn-lg pp-action-cta" onClick={onSchedule} type="button">
              <I.calendar size={14} /> {t('postPreview.scheduleThisPost')}
            </button>
            <div className="pp-action-row">
              <button className="btn btn-ghost btn-sm" onClick={onEdit} type="button">
                <I.edit size={12} /> {t('postPreview.edit')}
              </button>
              {/* Regenerate from this screen was removed — the action surfaced
                  here surfaced "An unexpected error occurred" in production
                  because it triggered the same heavy content-regenerate flow
                  outside the EditPost form context (no platform/language
                  scoping, no notes, no schedule confirmation). Users have a
                  cleaner Regenerate text + Regenerate image inside Edit
                  Post which IS instrumented for those signals. */}
              {!post.targetId && onDelete && <button className="btn btn-ghost btn-sm pp-danger" onClick={onDelete} type="button">
                <I.trash size={12} /> {t('postPreview.delete')}
              </button>}
            </div>
          </>
        )}
      </div>

      {/* Threads replies stay live; performance uses the canonical persisted
          content metrics below so the page has one refresh surface. */}
      {isPublished && post.platform === 'threads' && post.externalPostId && (
        <ThreadsRepliesSection key={`replies:${post.targetId}:${post.externalPostId}`} externalPostId={post.externalPostId} postOwnerHandle={post.handle} />
      )}

      {/* Instagram-only sections — visible after the post is published to
          Instagram. Pull live data from Meta Graph IG Business API via
          instagram-worker. */}
      {isPublished && post.platform === 'instagram' && post.externalPostId && (
        <InstagramCommentsSection key={`comments:${post.targetId}:${post.externalPostId}`} externalPostId={post.externalPostId} postOwnerHandle={post.handle} />
      )}

      {isPublished && <PersistedMetrics key={`${post.id}:${post.targetId}:${post.externalPostId}:${api.getReadOwnerScope()}`} post={post} onReload={onReload} />}

      <Metadata className="pp-section">
        {compactPreview
          ? <summary>{t('postPreview.details')}</summary>
          : <div className="eyebrow"><span>{t('postPreview.details')}</span></div>}
        <ul className="pp-detail-list">
          {!compactPreview && <li>
            <span className="pp-detail-l">{t('postPreview.platform')}</span>
            <span className="pp-detail-v">
              <span
                className={`ob-net-ic-${post.platform}`}
                style={{ width: 18, height: 18, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
              >
                {BrandIcons[post.platform]?.({ size: 11 })}
              </span>
              {platformDisplayName(post.platform)}
            </span>
          </li>}
          <li>
            <span className="pp-detail-l">{t('postPreview.media')}</span>
            <span className="pp-detail-v">
              {post.slides.some(slide => slide.isVideo)
                ? t('media.videoLabel', { defaultValue: 'Video' })
                : post.format === 'text_post' || post.slides.length === 0
                ? t('postPreview.textOnly')
                : post.slides.length === 1
                ? t('postPreview.singleImage')
                : t('postPreview.carouselSlides', { count: post.slides.length })}
            </span>
          </li>
          {post.altText && (
            <li className="pp-detail-stack">
              <span className="pp-detail-l">{t('postPreview.altText')}</span>
              <span className="pp-detail-v pp-detail-v-text">{post.altText}</span>
            </li>
          )}
          {post.createdAt && (
            <li>
              <span className="pp-detail-l">{t('postPreview.created')}</span>
              <span className="pp-detail-v">{fmtDate(post.createdAt)}</span>
            </li>
          )}
        </ul>
      </Metadata>
    </aside>
  );
}

/* ============================================================
   Main screen
   ============================================================ */

export const PostPreview = () => {
  const { t } = useTranslation();
  const dash = useDashboardData();
  const { openUpgrade } = useUpgrade();
  // GH-1133 S5 (§2.2 S8, §2.5): in the free preview every action that needs
  // the trial (publish, schedule, full edit, image, text-only release) opens the
  // trial wall before any request is sent, and Delete is hidden (archiving a
  // preview draft would leave the board's "3 of 3" slot empty for good).
  // Outside the preview previewGuard calls straight through.
  const freePreview = useFreePreview();
  const ownPublishing = freePublishingState(dash.entitlementsError ? null : dash.entitlements || freePreview.entitlements);
  const previewGuard = usePreviewGuard();
  const creationAllowed = Boolean(ownPublishing) || creationAccessState(dash) === 'allowed';
  const imageAllowance = deriveImageAllowance(dash.entitlements);
  const imageRemaining = dash.entitlements?.usage?.image_generations_monthly?.remaining;
  const imageExhausted = imageAllowance.limit !== null && Number.isFinite(imageRemaining) && imageRemaining <= 0;
  const imageGenerationAllowed = !ownPublishing && creationAllowed && imageAllowance.known && !imageAllowance.zero && !imageExhausted;
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const targetHint = useMemo(() => ({
    targetId: searchParams.get('target') || null,
    platform: searchParams.get('platform') || null,
  }), [searchParams]);
  const readScope = useSyncExternalStore(subscribeReadScope, getReadScope, getReadScope);
  const ownerScope = api.getReadOwnerScope();
  const readIdentity = JSON.stringify([ownerScope, dash.accountId || '', id, targetHint.targetId]);
  const readIdentityRef = useRef(readIdentity);
  readIdentityRef.current = readIdentity;
  useEffect(() => {
    readIdentityRef.current = readIdentity;
    return () => { readIdentityRef.current = null; };
  }, [readIdentity]);
  const navigate = useNavigate();
  const { setActivity } = useActivity();
  // Watch the global operations bag for an in-flight publish targeting
  // THIS content item. Match by sourceRef.id against either the
  // contentItem id (set up-front in EditPost) or the canonical
  // scheduled_post id (upgraded once the API returns).
  const { operationsArr, startOperation, failOperation } = useGenerationStatus();
  const publishingOp = useMemo(() => {
    const candidates = operationsArr.filter((op) =>
      op.kind === 'publish' &&
      (op.status === 'pending' || op.status === 'running') &&
      (op.contentId === id || op.sourceRef?.type === 'contentItem' && op.sourceRef.id === id)
    );
    return candidates[0] || null;
  }, [operationsArr, id]);
  // Refresh local `raw` shortly after a publish op terminates so the
  // status pill flips from "Publishing…" → "Published" without the user
  // having to F5. We also bust api.content.get's 30s cache because that
  // would otherwise hand back the same stale payload we initially
  // hydrated on mount.
  const lastTerminalSigRef = React.useRef('');
  useEffect(() => {
    // Match terminal publish ops belonging to this content item — by
    // op.contentId (always set at start in EditPost) so we don't miss
    // ops whose sourceRef has since been upgraded from contentItem →
    // scheduledPost by the bridge.
    const terminalOps = operationsArr.filter((op) =>
      op.kind === 'publish' &&
      (op.status === 'succeeded' || op.status === 'failed') &&
      op.contentId === id
    );
    if (!terminalOps.length) return;
    const sig = terminalOps.map((op) => `${op.id}:${op.status}:${op.updatedAt}`).sort().join('|');
    if (sig === lastTerminalSigRef.current) return;
    lastTerminalSigRef.current = sig;
    // Schedule two refreshes: an early one at 1.2s (catches the common
    // case where content_item.status flipped already) and a follow-up at
    // 4s (backstop for slower platforms where finalize lags). Both bust
    // the 30s GET cache otherwise the same stale payload comes back.
    const refresh = () => {
      if (!id) return;
      try { api.invalidateCache?.(`/api/content/${encodeURIComponent(id)}`); } catch (_) {}
      api.content.get(id, { targetId: targetHint.targetId, fresh: true }).then((res) => {
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        acceptRead(payload);
      }).catch(() => {});
    };
    const t1 = setTimeout(refresh, 1200);
    const t2 = setTimeout(refresh, 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [operationsArr, readIdentity, readScope]);

  const [snapshot, setSnapshot] = useState(null);
  // A cache generation invalidates callbacks, not an already readable post.
  // Ownership changes mask the previous snapshot in the very first render.
  const raw = snapshot?.identity === readIdentity ? snapshot.raw : null;
  const ownerIsCurrent = () => readIdentityRef.current === readIdentity && api.getReadOwnerScope() === ownerScope;
  const readIsCurrent = () => ownerIsCurrent() && api.getReadScope() === readScope;
  const setRaw = next => {
    if (!ownerIsCurrent()) return;
    setSnapshot(previous => ownerIsCurrent() ? {
      identity: readIdentity,
      raw: typeof next === 'function' ? next(previous?.identity === readIdentity ? previous.raw : null) : next,
    } : previous);
  };
  const acceptRead = (payload) => {
    if (!readIsCurrent()) return;
    setRaw(payload);
    setError(null);
  };
  const reloadMetrics = async () => {
    api.invalidateCache?.(`/api/content/${encodeURIComponent(id)}`);
    const res = await api.content.get(id, { targetId: targetHint.targetId, fresh: true });
    acceptRead(res?.data && typeof res.data === 'object' ? res.data : res);
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [captionSession, setCaptionSession] = useState(null);
  const ownDraftEditable = !targetHint.targetId && Boolean(ownPublishing) && canEditManualDraft(raw);
  const captionEditable = !ownPublishing && !targetHint.targetId && canEditPreviewCaption(freePreview, raw);
  const captionQueryOpened = useRef(null);
  useEffect(() => {
    if (captionEditable && searchParams.get('edit') === 'caption' && captionQueryOpened.current !== readIdentity) {
      captionQueryOpened.current = readIdentity;
      setCaptionSession({ identity: readIdentity, raw, caption: previewCaptionText(mapServerPost(raw, targetHint)) });
    }
  }, [readIdentity, captionEditable, searchParams, raw]);
  // First-post image (re)generation triggered from this screen — drives the
  // generating overlay + powers the "Generate image" action for an image-less
  // first post (e.g. one whose premium image was never requested).
  const [genImg, setGenImg] = useState(false);
  const [genImgErr, setGenImgErr] = useState(null);
  // Save-to-Media: ids saved this session (for the check state) + the id mid-save.
  const [savedAssetIds, setSavedAssetIds] = useState(() => new Set());
  const [savingAssetId, setSavingAssetId] = useState(null);
  const saveSlideToMedia = useCallback(async (slide) => {
    const mediaAssetId = slide?.mediaAssetId;
    if (!mediaAssetId) return;
    // GH-1133 S5: Save-to-Media writes /api/visual (card-gated) — the wall.
    if (freePreview.active) { freePreview.openTrialWall('image', { postId: id }); return; }
    setSavingAssetId(mediaAssetId);
    try {
      await api.brandAssets.saveFromMedia({ mediaAssetId });
      setSavedAssetIds((prev) => { const n = new Set(prev); n.add(mediaAssetId); return n; });
      notify({ level: 'success', title: t('postPreview.savedToMedia') });
    } catch (err) {
      notify({ level: 'error', title: t('postPreview.saveToMediaFailed', { error: friendlyErrorMessage(err) }) });
    } finally {
      setSavingAssetId(null);
    }
  }, [t, freePreview, id]);
  const mediaSaveValue = useMemo(
    () => ({ savedAssetIds, savingAssetId, saveSlideToMedia }),
    [savedAssetIds, savingAssetId, saveSlideToMedia]
  );
  // Delete-confirm state. MUST live with the top-level hooks (above the loading
  // / error early returns below): declaring it later, next to handleDelete,
  // changed the hook count between the loading and loaded renders and crashed
  // the page with React #310.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [linkCandidate, setLinkCandidate] = useState(null);
  const [linking, setLinking] = useState(false);
  const linkPendingRef = useRef(false);
  useEffect(() => { setLinkCandidate(null); }, [readIdentity]);
  // TASK-074 held-post release ("publish without image") confirm state. Also
  // top-level for the same React #310 reason as the delete-confirm hooks.
  const [confirmTextOnlyOpen, setConfirmTextOnlyOpen] = useState(false);
  const [allowingTextOnly, setAllowingTextOnly] = useState(false);

  // One-shot D1 lag refresh. EditPost adds ?just=scheduled to the URL
  // after a successful scheduler.create — that's our signal that the
  // current GET may race D1 replication (scheduler-worker just wrote
  // content_items.status='scheduled' but the content-worker read might
  // hit a replica that hasn't yet propagated). Refetch once after 1.5s
  // with the cache busted so the status pill flips "Ready" → "Scheduled"
  // even when the initial mount fetch lost the race. The hint is
  // self-cleaning: it only fires once per mount.
  const justSignal = searchParams.get('just') || '';
  useEffect(() => {
    if (!id) return;
    if (justSignal !== 'scheduled') return;
    const t = setTimeout(() => {
      try { api.invalidateCache?.(`/api/content/${encodeURIComponent(id)}`); } catch (_) {}
      api.content.get(id, { targetId: targetHint.targetId, fresh: true }).then((res) => {
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        acceptRead(payload);
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [readIdentity, justSignal, readScope]);

  // Refetch live status when the tab/window regains focus. Publishing can settle
  // while the user is on another tab or in Library; without this the post page
  // can keep showing a stale "failed"/"publishing" pill until a manual reload.
  useEffect(() => {
    if (!id) return;
    const refetch = () => {
      try { api.invalidateCache?.(`/api/content/${encodeURIComponent(id)}`); } catch (_) {}
      api.content.get(id, { targetId: targetHint.targetId, fresh: true })
        .then((res) => acceptRead(res?.data && typeof res.data === 'object' ? res.data : res))
        .catch(() => {});
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refetch(); };
    window.addEventListener('focus', refetch);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refetch);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [readIdentity, readScope]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const current = () => !cancelled && readIsCurrent();
    setLoading(!raw);
    setError(null);
    if (!raw) setRaw(null);
    // Always open the post on LIVE status. /api/content/:id is cached 30s, so
    // re-entering the page (e.g. Library -> back) within that window could show
    // a stale "failed"/"publishing" pill even though the publish has settled.
    // Bust the cache on every open so the status reflects reality.
    try { api.invalidateCache?.(`/api/content/${encodeURIComponent(id)}`); } catch (_) {}
    api.content.get(id, { targetId: targetHint.targetId, fresh: true })
      .then((res) => {
        if (!current()) return;
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        acceptRead(payload);
      })
      .catch(() => {
        if (!current()) return;
        if (targetHint.targetId) setRaw(null);
        setError(t('postPreview.couldNotLoadPost'));
      })
      .finally(() => {
        if (current()) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [readIdentity, readScope]);

  // While the job is still generating, the active version + slides don't
  // exist yet — the phone chrome would render empty. Poll the content
  // endpoint every 3s and swap to the real post as soon as the worker
  // finishes. After ready, keep polling for ~30s so the publish_target
  // status transition (scheduled → published) also lands without forcing
  // the user to refresh.
  const generationStatus = raw?.contentItem?.generationStatus || raw?.generationStatus || null;
  // Visual can still be cooking via ai-generation-queue even after text
  // generation flips the content_item's generation_status to 'ready'. In
  // that window the user used to see a fully-rendered preview with a
  // placeholder image — surface visual_status from the job snapshot so
  // we keep the generating overlay up until the image actually lands
  // (or fails). Defensive: only apply for image_post items, otherwise
  // text-only formats would never finalize.
  const jobFormat = raw?.job?.format || raw?.contentItem?.format;
  const jobVisualStatus = (raw?.job?.visualStatus || '').toLowerCase();
  // An EMPTY jobVisualStatus means there is NO active visual job for this item
  // (content_generation_jobs.visual_status is null, or no job row at all) — e.g.
  // a first post whose premium image was never requested. The backend treats
  // null as "no visual was ever requested" (terminal), so the UI must NOT spin
  // forever: only a real, non-terminal visual status counts as "still cooking".
  // A genuinely cooking New-Post image has visual_status='queued'/'running'.
  const visualStillCooking = jobFormat === 'image_post'
    && jobVisualStatus !== ''
    && !['succeeded', 'failed'].includes(jobVisualStatus)
    && !(raw?.activeVersion?.imageUrl)
    && !(Array.isArray(raw?.activeVersion?.mediaUrls) && raw.activeVersion.mediaUrls.length > 0);
  const isGenerating = genImg
    || visualStillCooking
    || (generationStatus && !['ready', 'failed'].includes(generationStatus));
  const isAwaitingPublish = !isGenerating
    && raw?.publishSummary
    && !raw.publishSummary.lastPublishedAt
    && Array.isArray(raw.publishSummary.targets)
    // Poll while ANY target is still in a non-terminal publish state. Status-based
    // (not priority==='now', which lingers on a settled target): 'publishing' is now
    // covered — the page could previously sit on "Publishing…" with no poll — and a
    // target that has reached 'failed'/'published' stops the loop instead of polling
    // a dead post.
    && raw.publishSummary.targets.some((t) => ['pending', 'queued', 'scheduled', 'publishing'].includes(t?.status));
  const shouldPoll = isGenerating || isAwaitingPublish;
  useEffect(() => {
    if (!id || !shouldPoll) return;
    let cancelled = false;
    // Publishing settles asynchronously (dispatch queue + per-platform API calls +
    // a possible token refresh), and a FAILURE must still surface without a manual
    // reload. Poll generously — up to 3 min — for the awaiting-publish window. The
    // old 30s cap expired before slow failures (e.g. X "token is not active") were
    // reflected, leaving the page stuck on "Publishing…".
    let stopAt = isGenerating ? Infinity : Date.now() + 180_000;
    const tick = () => {
      if (cancelled) return;
      if (Date.now() > stopAt) { clearInterval(interval); return; }
      // Bust the GET cache so each poll sees the live target status (not a stale
      // 30s-cached "publishing" payload).
      try { api.invalidateCache?.(`/api/content/${encodeURIComponent(id)}`); } catch (_) {}
      api.content.get(id, { targetId: targetHint.targetId, fresh: true })
        .then((res) => {
          if (cancelled) return;
          const payload = res?.data && typeof res.data === 'object' ? res.data : res;
          acceptRead(payload);
        })
        .catch(() => { /* swallow; next tick may succeed */ });
    };
    const interval = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [readIdentity, shouldPoll, isGenerating, readScope]);

  const mappedPost = useMemo(() => mapServerPost(raw, targetHint), [raw, targetHint]);
  const brandProfileId = raw?.contentItem?.brandProfileId;
  const needsBrandIdentity = Boolean(mappedPost && !mappedPost.handle && brandProfileId);
  const [brandSnapshot, setBrandSnapshot] = useState(null);
  useEffect(() => {
    if (!needsBrandIdentity) return;
    let cancelled = false;
    api.brands.list().then(response => {
      if (cancelled || !readIsCurrent()) return;
      const data = response?.data || response;
      setBrandSnapshot({ identity: readIdentity, brandProfileId, brands: data?.brands });
    }).catch(() => { /* Keep the neutral author when the saved brand cannot be read. */ });
    return () => { cancelled = true; };
  }, [readIdentity, readScope, brandProfileId, needsBrandIdentity]);
  const post = mappedPost && {
    ...mappedPost,
    ...postPreviewAuthor(mappedPost, brandProfileId,
      brandSnapshot?.identity === readIdentity && brandSnapshot.brandProfileId === brandProfileId
        ? brandSnapshot.brands : null, t('platformCards.yourBrand')),
  };
  const selectedPublicationUnavailable = Boolean(targetHint.targetId && !post);

  useEffect(() => { setSlideIdx(0); setLightbox(false); }, [readIdentity]);

  if (loading || snapshot?.identity !== readIdentity) {
    return (
      <div className="pp-root">
        <div className="pp-top">
          <div>
            <Link to="/app/library" className="pp-back"><I.chevLeft size={14} /> {t('postPreview.library')}</Link>
            <h1 className="pp-h">{t('postPreview.loading')}</h1>
          </div>
        </div>
      </div>
    );
  }
  if (!post) {
    return (
      <div className="pp-root">
        <div className="pp-top">
          <div>
            <Link to="/app/library" className="pp-back"><I.chevLeft size={14} /> {t('postPreview.library')}</Link>
            <h1 className="pp-h">{t(selectedPublicationUnavailable ? 'postPreview.selectedPublicationUnavailable' : 'postPreview.postNotFound')}</h1>
            <p className="pp-sub">{error || t(selectedPublicationUnavailable ? 'postPreview.selectedPublicationUnavailableSub' : 'postPreview.postNotFoundSub')}</p>
            {selectedPublicationUnavailable && <button className="btn btn-ghost btn-sm" onClick={() => reloadMetrics().catch(() => setError(t('postPreview.couldNotLoadPost')))} type="button">
              <I.refresh size={13} /> {t('postPreview.refresh')}
            </button>}
          </div>
        </div>
      </div>
    );
  }

  // Job is still running — render a phone chrome at the same dimensions as
  // the real post preview, but with shimmer placeholders inside. The phone
  // sits in a .pp-phone wrapper (the same one the real preview uses) so the
  // loading state literally morphs into the post when generation finishes.
  if (isGenerating) {
    const batchId = raw?.contentItem?.generationBatchId || raw?.generationBatchId || raw?.batch?.id || null;
    const stage = generationStatus === 'visualizing' ? t('postPreview.stageGeneratingImage')
      : generationStatus === 'drafting' ? t('postPreview.stageWritingDraft')
      : t('postPreview.stageWorkingOnIt');
    return (
      <div className="pp-root pp-root-generating">
        <div className="pp-top">
          <div>
            <Link to="/app/library" className="pp-back"><I.chevLeft size={14} /> {t('postPreview.library')}</Link>
          </div>
        </div>
        <div className="pp-generating-stage" role="status" aria-live="polite">
          <div className="pp-phone">
            <div className="pp-phone-shell">
              <div className="pp-phone-notch" />
              <div className="pp-phone-screen pp-phone-screen-loading">
                <div className="pp-phone-status">
                  <span>9:41</span>
                  <span className="pp-phone-status-r">•••</span>
                </div>
                <div className="pp-loading-header" aria-hidden="true">
                  <div className="pp-loading-avatar" />
                  <div className="pp-loading-handle">
                    <div className="pp-loading-pill pp-loading-pill-w50" />
                    <div className="pp-loading-pill pp-loading-pill-w30" />
                  </div>
                </div>
                <div className="pp-loading-media" aria-hidden="true">
                  <div className="pp-generating-spinner" />
                </div>
                <div className="pp-loading-body" aria-hidden="true">
                  <div className="pp-loading-pill pp-loading-pill-w90" />
                  <div className="pp-loading-pill pp-loading-pill-w70" />
                  <div className="pp-loading-pill pp-loading-pill-w50" />
                </div>
                <div className="pp-phone-home" />
              </div>
            </div>
          </div>
          <div className="pp-generating-caption">
            <div className="pp-generating-status" aria-hidden="true">
              <span className="pp-generating-pulse-dot" />
              <span className="pp-generating-eyebrow">{t('postPreview.generating')}</span>
            </div>
            <h2 className="pp-generating-h">
              {stage}<span className="pp-generating-dots" aria-hidden="true">…</span>
            </h2>
            <p className="pp-generating-sub">
              {t('postPreview.generatingSub')}
            </p>
            {batchId && (
              <button
                type="button"
                className="pp-generating-cta"
                onClick={() => navigate(`/app/batch/${encodeURIComponent(batchId)}`)}
              >
                <I.chevLeft size={14} style={{ transform: 'rotate(180deg)' }} />
                {t('postPreview.viewBatchProgress')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Extract a useful error message from API errors (which wrap the original).
  const errMessage = (err) => {
    if (!err) return t('postPreview.unknownError');
    return err?.body?.error?.message
      || err?.body?.message
      || err?.data?.message
      || err?.message
      || t('postPreview.unknownError');
  };

  // Activity "Open" link for a publish op. Carries the same platform/target
  // pair the op is sent for — on a bare /app/post/<id> the preview falls back
  // to the first published sibling, not the target being retried (GH-976).
  const publishOpDeeplink = () => {
    const qs = new URLSearchParams();
    if (post.platform) qs.set('platform', post.platform);
    if (post.targetId) qs.set('target', post.targetId);
    const query = qs.toString();
    return `/app/post/${encodeURIComponent(post.id)}${query ? `?${query}` : ''}`;
  };

  // "Publish now" — for scheduled posts. Reschedule to "now" so the dispatcher
  // sweep picks it up immediately. Falls back to retry() for failed targets.
  const handlePublishNow = async () => {
    if (!post.scheduledPostId) {
      notify({ level: 'warning', title: t('postPreview.noScheduledPostScheduleFirst') });
      return;
    }
    // Mint a publish op so the global plate + DetailsPanel "Publishing…"
    // overlay light up immediately. The ContentFeed bridge will flip it
    // to succeeded/failed once publish_targets transition.
    const platforms = post.platform ? [post.platform] : [];
    const platformStatus = {};
    platforms.forEach((p) => { platformStatus[p] = 'running'; });
    const opId = startOperation({
      kind: 'publish',
      label: t('postPreview.publishingToPlatform', { platform: platforms[0] ? platformDisplayName(platforms[0]) : t('postPreview.platformGeneric') }),
      subtype: 'publish_now',
      status: 'running',
      total: 1,
      completed: 0,
      platforms,
      platformStatus,
      sourceRef: { type: 'scheduledPost', id: post.scheduledPostId },
      deeplink: publishOpDeeplink(),
      contentId: post.id,
      expectedDurationMs: 15_000,
    });
    const nowIso = new Date().toISOString();
    try {
      await api.scheduler.reschedule(post.scheduledPostId, { scheduledAt: nowIso });
    } catch (err) {
      try {
        if (!post.targetId) throw err;
        await api.scheduler.retry(post.scheduledPostId, { targetId: post.targetId });
      } catch (err2) {
        failOperation(opId, errMessage(err2) || errMessage(err) || t('postPreview.publishFailed'));
        return;
      }
    }
  };

  // "Retry publish" — for failed targets. Fires the existing scheduler
  // retry endpoint scoped to the selected target. Other platforms, including
  // unresolved publications, must not be sent again by this action. Wraps
  // in a GenerationStatusContext op so the plate shows progress just like
  // a fresh publish_now.
  const handleRetry = async () => {
    if (!post.scheduledPostId) {
      notify({ level: 'warning', title: t('postPreview.noScheduledPost') });
      return;
    }
    if (!post.targetId) {
      handleRepublish();
      return;
    }
    const platforms = post.platform ? [post.platform] : [];
    const platformStatus = {};
    platforms.forEach((p) => { platformStatus[p] = 'running'; });
    const opId = startOperation({
      kind: 'publish',
      label: t('postPreview.retryingPublishToPlatform', { platform: platforms[0] ? platformDisplayName(platforms[0]) : t('postPreview.platformGeneric') }),
      subtype: 'publish_now',
      status: 'running',
      total: 1,
      completed: 0,
      platforms,
      platformStatus,
      sourceRef: { type: 'scheduledPost', id: post.scheduledPostId },
      deeplink: publishOpDeeplink(),
      contentId: post.id,
      expectedDurationMs: 15_000,
    });
    try {
      await api.scheduler.retry(post.scheduledPostId, { targetId: post.targetId });
    } catch (err) {
      failOperation(opId, errMessage(err) || t('postPreview.retryFailed'));
    }
  };

  // "Edit & republish" — navigate to EditPost's Schedule tab where the user
  // can review the copy, pick which accounts/platforms to publish to, and
  // confirm. Previously this button silently cloned the scheduled_post and
  // published to the SAME platform immediately, which surprised users who
  // expected "republish" to mean "republish FROM SCRATCH" (different account,
  // edited copy, different schedule). The cleaner UX: take the user inside
  // EditPost where every dimension is editable, then they save → publish
  // through the normal flow. Forwards the existing platform/target hints so
  // EditPost opens scoped to the same chrome the user was viewing.
  const handleRepublish = () => {
    const qs = new URLSearchParams();
    qs.set('tab', 'schedule');
    if (post.platform) qs.set('platform', post.platform);
    if (post.targetId) qs.set('target', post.targetId);
    navigate(`/app/post/${encodeURIComponent(post.id)}/edit?${qs.toString()}`);
  };
  // Both "Schedule this post" (on a draft) and "Reschedule" (on a scheduled
  // post) deep-link into EditPost's schedule tab — that's where the actual
  // scheduling form + Save logic live. The previous /app/calendar?content=…
  // route had no handler for the query string, so the click went nowhere.
  // Forward the platform hint (and target hint) so EditPost can scope the
  // chrome + per-account picker to the same platform the user is viewing.
  // Without this, a Telegram preview opening Edit would surface ALL accounts
  // from the batch (including IG/Threads peers), which is the wrong scope.
  const editQs = (() => {
    const qs = new URLSearchParams();
    if (targetHint.platform) qs.set('platform', targetHint.platform);
    if (targetHint.targetId) qs.set('target', targetHint.targetId);
    return qs.toString();
  })();
  const editSuffix = editQs ? `&${editQs}` : '';
  const handleSchedule = () => navigate(`/app/post/${encodeURIComponent(post.id)}/edit?tab=schedule${editSuffix}`);
  const handleEdit = () => navigate(`/app/post/${encodeURIComponent(post.id)}/edit${editQs ? `?${editQs}` : ''}`);
  const handleReschedule = () => navigate(`/app/post/${encodeURIComponent(post.id)}/edit?tab=schedule${editSuffix}`);
  // Delete uses the styled ConfirmDialog instead of a raw window.confirm.
  // (confirmDeleteOpen / deleting are declared with the top-level hooks above.)
  const handleDelete = () => {
    if (post.targetId || post.status === 'published') return;
    setConfirmDeleteOpen(true);
  };
  const linkExistingPublication = async () => {
    if (linkPendingRef.current || !linkCandidate || !post.targetId
      || linkCandidate.sourceTargetId !== post.targetId || linkCandidate.readIdentity !== readIdentity
      || !ownerIsCurrent()) return;
    const candidate = linkCandidate.candidate;
    if (candidate.targetId === post.targetId || candidate.platform !== post.platform
      || candidate.socialAccountId !== post.accountId || !candidate.publishedAt) return;
    linkPendingRef.current = true;
    setLinking(true);
    try {
      await api.scheduler.linkRecovery({
        sourceTargetId: post.targetId,
        successorTargetId: candidate.targetId,
        confirmUnpublished: true,
      });
      if (ownerIsCurrent()) await reloadMetrics();
      if (ownerIsCurrent()) setLinkCandidate(null);
    } catch (err) {
      if (ownerIsCurrent()) notify({ level: 'error', title: t('postPreview.recoveryLinkFailed'), error: err });
    } finally {
      linkPendingRef.current = false;
      if (ownerIsCurrent()) setLinking(false);
    }
  };
  const performDelete = async () => {
    if (post.targetId || post.status === 'published') return;
    setDeleting(true);
    try {
      await api.content.archive(post.id);
      navigate('/app/library');
    } catch (err) {
      notify({ level: 'error', title: t('postPreview.couldNotDelete'), error: err });
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };
  // Generate (or retry) the premium image for an image-less first post. The
  // backend self-heals: it finds the existing first post and attaches JUST the
  // image, keeping the copy. genImg drives the generating overlay while it runs.
  const handleGenerateFirstPostImage = async () => {
    if (genImg || !imageGenerationAllowed) return;
    setGenImg(true);
    setGenImgErr(null);
    try {
      const res = await api.content.generateFirstPost({ includePremiumImage: true, retryImage: true });
      try { api.invalidateCache?.(`/api/content/${encodeURIComponent(id)}`); } catch (_) {}
      const fresh = await api.content.get(id, { targetId: targetHint.targetId, fresh: true });
      setRaw(fresh?.data && typeof fresh.data === 'object' ? fresh.data : fresh);
      // The backend swallows image-step failures into a non-null imageWarning
      // (the response still "succeeds"). Surface it instead of silently bouncing
      // back to a still-image-less post — so the real reason is visible.
      const warn = res?.firstPost?.imageWarning || res?.imageWarning;
      if (warn) setGenImgErr(warn);
    } catch (err) {
      if (err?.code === 'PLAN_LAPSED') {
        dash.invalidateResource?.('entitlements', { fresh: true });
        openUpgrade({ feature: 'default', variant: 'renew' });
        setGenImgErr(t('paymentIssue.lapsedBody'));
      } else setGenImgErr(errMessage(err) || t('postPreview.imageGenFailed', 'Image generation failed'));
    } finally {
      setGenImg(false);
    }
  };

  // TASK-074 — HELD post: text is ready but the visual failed (generation
  // error or plan limit) and no media is attached, so the publish gate keeps
  // it as a draft. Two ways out: attach an image (existing EditPost media
  // flow) or explicitly opt in to a text-only publish below.
  const heldAwaitingImage = isRawContentHeldAwaitingImage(raw);
  const handleHeldAddImage = () => navigate(`/app/post/${encodeURIComponent(post.id)}/edit?tab=media${editSuffix}`);
  const performAllowTextOnly = async () => {
    setAllowingTextOnly(true);
    try {
      await api.content.postAllowTextOnly(post.id);
      // Optimistic: flip the flag locally so the held banner clears at once;
      // the follow-up fresh GET confirms the released state server-side.
      setRaw((prev) => (prev ? {
        ...prev,
        contentItem: prev.contentItem ? { ...prev.contentItem, allowTextOnly: true } : prev.contentItem,
      } : prev));
      setConfirmTextOnlyOpen(false);
      notify({
        level: 'success',
        title: t('postPreview.heldReleasedTitle'),
        description: t('postPreview.heldReleasedMsg'),
      });
      try { api.invalidateCache?.(`/api/content/${encodeURIComponent(post.id)}`); } catch (_) {}
      api.content.get(post.id, { targetId: targetHint.targetId, fresh: true }).then((res) => {
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setRaw(payload);
      }).catch(() => {});
    } catch (err) {
      notify({ level: 'error', title: t('postPreview.heldReleaseFailed'), error: err });
      setConfirmTextOnlyOpen(false);
    } finally {
      setAllowingTextOnly(false);
    }
  };

  const wallMeta = {
    postId: post.id,
    thumbUrl: post.slides.find((slide) => slide.imageUrl)?.imageUrl || null,
    thumbTitle: post.title || null,
  };

  // handleRegenerate was removed alongside the button on the
  // DetailsPanel action row. Same logic lives inside EditPost where
  // it can take per-platform + language scoping. If we ever want to
  // restore a "quick regen" entry point here, do it as a deeplink to
  // /app/post/:id/edit?tab=copy with autorun=1 — don't call the API
  // directly from this screen.

  return (
    <div className="pp-root">
      {!ownPublishing && <CreationAccessNotice />}
      {error && <p className="metric-feedback" role="status">{error}</p>}
      {freePreview.active && freePreview.mode === 'active' && !freePreview.readOnly && captionSession?.identity === readIdentity && (
        <PreviewCaptionEditor
          key={readIdentity}
          raw={captionSession.raw}
          caption={captionSession.caption}
          onCancel={() => setCaptionSession(null)}
          onSaved={() => {
            if (!ownerIsCurrent()) return;
            setCaptionSession(null);
            notify({ level: 'success', title: t('freePreview.captionSaved') });
            reloadMetrics().catch(() => setError(t('postPreview.couldNotLoadPost')));
          }}
          onConflict={() => { reloadMetrics().catch(() => {}); }}
        />
      )}
      <div className="pp-top">
        <div>
          <Link to="/app/library" className="pp-back"><I.chevLeft size={14} /> {t('postPreview.library')}</Link>
          <h1 className="pp-h">{t('postPreview.postPreview')}</h1>
          <p className="pp-sub">
            {/* Drop the "Tap the cover for full-screen" hint when the
                post has no cover image — clicking would do nothing,
                so the hint is misleading. */}
            {post.slides.some((s) => s.imageUrl)
              ? t('postPreview.subWithCover')
              : t('postPreview.subNoCover')}
          </p>
        </div>
      </div>

      {freePreview.active && !ownPublishing && !captionEditable && (
        <div className="fp-draft-note fp-draft-note--post">
          <span className="pill">{t('reveal.draftBadge')}</span>
          <p className="fp-lockline"><Icon name="lock" size={12} /> {t('freePreview.actionHint')}</p>
          <CopyTextButton text={post.caption || ''} />
        </div>
      )}

      {/* TASK-074 — HELD post banner: image generation failed (or hit the plan
          limit) and the post is waiting for a visual before it can publish.
          Mirrors the first-post banner idiom below; actions reuse the existing
          EditPost media flow ("Add image") or release the post text-only. */}
      {!ownPublishing && heldAwaitingImage && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '0 0 16px', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)' }}>
          <span className="pill pill-warning" style={{ flex: '0 0 auto' }}>
            <I.image size={11} /> {t('postPreview.heldBadge')}
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: 13, flex: '1 1 240px' }}>
            {t('postPreview.heldExplain')}
          </span>
          <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={previewGuard('image', handleHeldAddImage, wallMeta)}>
              <I.image size={12} /> {t('postPreview.heldAddImage')}
            </button>
            <button
              type="button"
              className="btn btn-soft btn-sm"
              onClick={previewGuard('publish', () => setConfirmTextOnlyOpen(true), wallMeta)}
              disabled={allowingTextOnly}
            >
              {t('postPreview.heldPublishTextOnly')}
            </button>
          </div>
        </div>
      )}

      {/* Image-less first post (its premium image was never generated): offer a
          one-click (re)generate instead of leaving the post pictureless. */}
      {!ownPublishing && !heldAwaitingImage && raw?.contentItem?.source === 'first_post'
        && raw?.contentItem?.format === 'image_post'
        && !post.slides.some((s) => s.imageUrl) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '0 0 16px', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 13, flex: '1 1 auto' }}>
            {t('postPreview.noImageYet', "This post doesn't have an image yet.")}
          </span>
          <button type="button" className="pp-generating-cta" onClick={previewGuard('image', handleGenerateFirstPostImage, wallMeta)} disabled={genImg || (!freePreview.active && !imageGenerationAllowed)}>
            {genImg ? t('postPreview.generatingImage', 'Generating image…') : t('postPreview.generateImage', 'Generate image')}
          </button>
          {creationAllowed && !imageGenerationAllowed && <span role="status">
            {t(!imageAllowance.known ? 'imageLocked.loading' : imageAllowance.zero ? 'imageLocked.lockedLabel' : 'brandkit.genLimit')}
          </span>}
          {genImgErr && <span style={{ color: 'var(--danger, #e0685f)', fontSize: 12, width: '100%' }}>{genImgErr}</span>}
        </div>
      )}

      <MediaSaveContext.Provider value={mediaSaveValue}>
      <div className="pp-grid">
        <PhoneFrame
          post={post}
          slideIdx={slideIdx}
          setSlideIdx={setSlideIdx}
          openLightbox={() => setLightbox(true)}
        />
        <DetailsPanel
          compactPreview={captionEditable && !ownDraftEditable}
          onReload={reloadMetrics}
          post={post}
          publishingOp={publishingOp}
          onPublishNow={ownDraftEditable ? handleEdit : previewGuard('publish', handlePublishNow, wallMeta)}
          onRepublish={previewGuard('publish', handleRepublish, wallMeta)}
          onSchedule={previewGuard('schedule', handleSchedule, wallMeta)}
          onEdit={ownDraftEditable ? handleEdit : captionEditable ? () => setCaptionSession({ identity: readIdentity, raw, caption: previewCaptionText(post) }) : previewGuard('edit', handleEdit, wallMeta)}
          onReschedule={ownPublishing ? () => navigate('/app/calendar') : previewGuard('schedule', handleReschedule, wallMeta)}
          onRetry={previewGuard('publish', handleRetry, wallMeta)}
          onDelete={freePreview.active || ownPublishing ? null : handleDelete}
          onLinkExisting={(candidate) => setLinkCandidate({ candidate, sourceTargetId: post.targetId, readIdentity })}
        />
      </div>

      {lightbox && (
        <Lightbox
          post={post}
          slideIdx={slideIdx}
          setSlideIdx={setSlideIdx}
          onClose={() => setLightbox(false)}
        />
      )}
      </MediaSaveContext.Provider>
      <ConfirmDialog
        open={confirmTextOnlyOpen}
        tone="warn"
        title={t('postPreview.heldConfirmTitle')}
        description={t('postPreview.heldConfirmBody')}
        confirmLabel={t('postPreview.heldConfirmCta')}
        cancelLabel={t('common.cancel')}
        confirming={allowingTextOnly}
        onConfirm={performAllowTextOnly}
        onCancel={() => setConfirmTextOnlyOpen(false)}
      />
      <ConfirmDialog
        open={Boolean(linkCandidate)}
        tone="warn"
        title={t('postPreview.useExistingPublication')}
        description={<span className="pp-recovery-confirm-copy">{t('postPreview.useExistingPublicationConfirm', { account: linkCandidate?.candidate?.accountLabel || post.handle, date: fmtDate(linkCandidate?.candidate?.publishedAt) })}</span>}
        confirmLabel={t('postPreview.useExistingPublication')}
        cancelLabel={t('common.cancel')}
        confirming={linking}
        onConfirm={linkExistingPublication}
        onCancel={() => { if (!linking) setLinkCandidate(null); }}
      />
      <ConfirmDialog
        open={confirmDeleteOpen}
        tone="danger"
        title={t('common.delete')}
        description={t('postPreview.deleteConfirm')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirming={deleting}
        onConfirm={performDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </div>
  );
};

export default PostPreview;
